import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import { useAuth } from "../auth/authStore";
import { useNotifications } from "../notifications/notificationsStore";
import { profileApi } from "../api/profileApi";
import { appointmentsApi } from "../api/appointmentsApi";
import { paymentsApi } from "../api/paymentsApi";
import { psychologistsApi } from "../api/psychologistsApi";
import { podcastsApi } from "../api/podcastsApi";
import { literatureApi } from "../api/literatureApi";
import { blogApi } from "../api/blogApi";
import { forumApi } from "../api/forumApi";
import { notificationsApi } from "../api/notificationsApi";
import { onboardingApi } from "../api/onboardingApi";
import { pwaApi } from "../api/pwaApi";
import { dictApi } from "../api/dictApi";
import { filesApi } from "../api/filesApi";
import { request } from "../api/http";
import { useToast } from "../ui/toast/ToastProvider";
import { sanitizeFullNameInput, validateFullName } from "../utils/fullName";
import { applyThemeToDocument, persistTheme, readThemeFromStorage } from "../utils/theme";
import { sanitizeLinkHref } from "../utils/safeUrl";
import { resolveCalendarErrorMessage } from "../utils/userFacingApiError";
import { QUASAR_INTEGRATION_ENABLED } from "../config/env";

const BASE_PROFILE_TABS = [
  { id: "profile", label: "Профиль" },
  { id: "appointments", label: "Записи и платежи" },
  { id: "subscriptions", label: "Подписки" },
  { id: "content", label: "Мой контент" },
  { id: "integrations", label: "Интеграции" },
  { id: "settings", label: "Настройки" },
];
const PSYCHOLOGIST_PROFILE_TAB = { id: "psychologist", label: "Профиль психолога" };

const COMMENT_FILTERS = [
  { id: "all", label: "Все" },
  { id: "blog", label: "Блог" },
  { id: "forum", label: "Форум" },
  { id: "podcast", label: "Подкасты" },
  { id: "literature", label: "Литература" },
];

const NOTIFICATION_TOGGLES = [
  { key: "chatMessage", icon: "💬", title: "Чат", subtitle: "Сообщения от психолога" },
  { key: "sessionReminder", icon: "📅", title: "Сессии", subtitle: "Напоминания о записях" },
  { key: "blogNewPost", icon: "📝", title: "Блог и статьи", subtitle: "Новые посты от подписок" },
  { key: "podcastEpisodeNew", icon: "🎙️", title: "Подкасты", subtitle: "Новые эпизоды" },
  { key: "forumCommentOnMyPublication", icon: "🗨️", title: "Форум", subtitle: "Ответы и упоминания" },
];

const QUASAR_NOTIFICATION_FIELDS = [
  {
    key: "bookingPaid",
    label: "Записались на сессию",
    description: "Индивидуальную или групповую после оплаты",
  },
  {
    key: "favoriteAdded",
    label: "Добавили в избранное",
    description: "Когда профиль добавлен в избранное",
  },
  {
    key: "chatMessage",
    label: "Сообщение в чате",
    description: "Новые сообщения клиента",
  },
  {
    key: "sessionReminder",
    label: "Напоминание о сессии",
    description: "Перед началом сессии",
  },
];

const GOOGLE_SYNC_DIRECTION_OPTIONS = [
  { value: "TO_GOOGLE", label: "Только сессии Бюро Души -> Google Calendar" },
  { value: "FROM_GOOGLE", label: "Только занятость из Google Calendar" },
  { value: "BIDIRECTIONAL", label: "Рекомендуется: сессии -> Google, Google -> занятость" },
];

const YANDEX_SYNC_DIRECTION_OPTIONS = [
  { value: "TO_YANDEX", label: "Только сессии Бюро Души -> Яндекс Календарь" },
  { value: "FROM_YANDEX", label: "Только занятость из Яндекс Календаря" },
  { value: "BOTH", label: "Рекомендуется: сессии -> Яндекс, Яндекс -> занятость" },
];

const GOOGLE_SYNC_CONFLICT_OPTIONS = [
  { value: "SKIP_IF_BOOKED", label: "Пропускать, если уже есть запись" },
  { value: "FORCE_BREAK", label: "Помечать занятость даже при записи" },
];

const CALENDAR_OAUTH_PROVIDER_KEY = "settings.pendingCalendarProvider";
const PERSONAL_DATA_DOCUMENT_URL = "/documents/personal-data";
const PRIVACY_POLICY_DOCUMENT_URL = "/documents/privacy-policy";
const GOOGLE_CALENDAR_CONSENT_VERSION = "v1_8";
const YANDEX_CALENDAR_CONSENT_VERSION = "v1_8";
const GOOGLE_CALENDAR_CONSENT_TEXT =
  "Я даю согласие на обработку данных моего Google Calendar в целях синхронизации расписания с Платформой «Бюро Души», включая трансграничную передачу данных Google LLC (США), в соответствии с разделом о Google Calendar в Согласии на обработку персональных данных и Политикой конфиденциальности.";
const YANDEX_CALENDAR_CONSENT_TEXT =
  "Я даю согласие на обработку данных моего Яндекс Календаря в целях синхронизации расписания с Платформой «Бюро Души» в соответствии с разделом о Яндекс Календаре в Согласии на обработку персональных данных и Политикой конфиденциальности.";

const resolveConsentSourcePage = () => {
  if (typeof window === "undefined") {
    return "/dashboard/settings?tab=integrations";
  }
  const path = String(window.location.pathname || "/dashboard/settings").trim() || "/dashboard/settings";
  const search = String(window.location.search || "").trim();
  return `${path}${search}`;
};

const CHAT_POLICY_ANY_AUTHORIZED = "ANY_AUTHORIZED";
const CHAT_POLICY_ONLY_CLIENTS_WITH_PAID_HISTORY = "ONLY_CLIENTS_WITH_PAID_HISTORY";
const CHAT_POLICY_NOBODY = "NOBODY";
const CHAT_POLICY_VALUES = new Set([
  CHAT_POLICY_ANY_AUTHORIZED,
  CHAT_POLICY_ONLY_CLIENTS_WITH_PAID_HISTORY,
  CHAT_POLICY_NOBODY,
]);

const CHAT_POLICY_OPTIONS = [
  {
    key: CHAT_POLICY_ANY_AUTHORIZED,
    title: "Любой авторизованный",
    subtitle: "Писать может любой пользователь с аккаунтом.",
    badge: "Открытый доступ",
    accent: "mint",
  },
  {
    key: CHAT_POLICY_ONLY_CLIENTS_WITH_PAID_HISTORY,
    title: "Только клиенты с историей",
    subtitle: "Только клиенты с оплаченной или завершенной историей.",
    badge: "Проверенные клиенты",
    accent: "lav",
  },
  {
    key: CHAT_POLICY_NOBODY,
    title: "Никто",
    subtitle: "Личные сообщения закрыты для клиентов.",
    badge: "Чат закрыт",
    accent: "slate",
  },
];

const SOCIAL_NETWORK_OPTIONS = [
  { type: "telegram", label: "Telegram", icon: "✈️", placeholder: "https://t.me/username" },
  { type: "instagram", label: "Instagram", icon: "📸", placeholder: "https://instagram.com/username" },
  { type: "vk", label: "VK", icon: "🔵", placeholder: "https://vk.com/username" },
  { type: "whatsapp", label: "WhatsApp", icon: "💬", placeholder: "https://wa.me/79990000000" },
  { type: "youtube", label: "YouTube", icon: "▶️", placeholder: "https://youtube.com/@channel" },
  { type: "tiktok", label: "TikTok", icon: "🎵", placeholder: "https://tiktok.com/@username" },
  { type: "facebook", label: "Facebook", icon: "📘", placeholder: "https://facebook.com/username" },
  { type: "linkedin", label: "LinkedIn", icon: "💼", placeholder: "https://linkedin.com/in/username" },
  { type: "website", label: "Сайт", icon: "🌐", placeholder: "https://example.com" },
];

const SETTINGS_PAGE_SIZES = {
  upcomingAppointments: 5,
  pastAppointments: 6,
  payments: 8,
  psychSubscriptions: 6,
  favorites: 6,
  blogAuthors: 6,
  podcastSubscriptions: 4,
  savedBooks: 4,
  forumTopics: 4,
  blogPosts: 4,
  comments: 6,
};

const SETTINGS_PAGE_KEYS = Object.keys(SETTINGS_PAGE_SIZES);
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;
const COVER_MAX_BYTES = 8 * 1024 * 1024;
const AVATAR_MIN_SIDE_PX = 320;
const COVER_MIN_WIDTH_PX = 1200;
const COVER_MIN_HEIGHT_PX = 300;
const ALLOWED_IMAGE_MIME_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

function createInitialPageState() {
  return SETTINGS_PAGE_KEYS.reduce((acc, key) => {
    acc[key] = 1;
    return acc;
  }, {});
}

function extractUploadedFileUrl(payload) {
  const source = asObject(payload);
  const file = asObject(source.file);
  return cleanText(
    source.url ||
      source.avatarUrl ||
      source.documentUrl ||
      source.imageUrl ||
      file.url ||
      file.path
  );
}

function detectDocumentMediaType(urlValue, fileNameValue = "", mimeTypeValue = "") {
  const url = String(urlValue || "").trim();
  const lowerUrl = url.toLowerCase();
  let decodedUrl = lowerUrl;
  try {
    decodedUrl = decodeURIComponent(lowerUrl);
  } catch {
    decodedUrl = lowerUrl;
  }

  const fileName = String(fileNameValue || "").trim().toLowerCase();
  const mimeType = String(mimeTypeValue || "").trim().toLowerCase();

  const hasPdfExt = /\.pdf(?:[?#].*)?$/i.test(url) || /\.pdf$/i.test(fileName);
  const hasImageExt =
    /\.(png|jpe?g|gif|webp|svg)(?:[?#].*)?$/i.test(url) ||
    /\.(png|jpe?g|gif|webp|svg)$/i.test(fileName);

  const urlHintsPdf =
    /application(?:%2f|\/)pdf/i.test(lowerUrl) ||
    /[?&](?:response-content-type|content-type|mime(?:type)?|type)=application(?:%2f|\/)pdf/i.test(decodedUrl);
  const urlHintsImage =
    /image(?:%2f|\/)(?:png|jpe?g|gif|webp|svg\+xml)/i.test(lowerUrl) ||
    /[?&](?:response-content-type|content-type|mime(?:type)?|type)=image(?:%2f|\/)(?:png|jpe?g|gif|webp|svg\+xml)/i.test(decodedUrl);

  if (mimeType.includes("pdf") || hasPdfExt || urlHintsPdf) return "pdf";
  if (mimeType.startsWith("image/") || hasImageExt || urlHintsImage) return "image";
  return "other";
}

function canEmbedDocumentFrame(urlValue) {
  const raw = String(urlValue || "").trim();
  if (!raw) return false;
  if (/^(data:|blob:)/i.test(raw)) return true;
  if (typeof window === "undefined") return false;
  try {
    const parsed = new URL(raw, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
}

function CalendarConsentCard({ checked, disabled, text, version, onToggle }) {
  return (
    <div className="calendar-consent-card">
      <label className={`calendar-consent-card__toggle ${disabled ? "is-disabled" : ""}`}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onToggle?.(e.target.checked)}
        />
        <span>{text}</span>
      </label>
      <div className="calendar-consent-card__meta">
        <span>Версия документа: <strong>{version}</strong></span>
        <span>
          <Link to={PERSONAL_DATA_DOCUMENT_URL}>Согласие на обработку ПД</Link>
          {" "}и{" "}
          <Link to={PRIVACY_POLICY_DOCUMENT_URL}>Политика конфиденциальности</Link>
        </span>
      </div>
    </div>
  );
}

function imageTypeAllowed(file) {
  const type = String(file?.type || "").toLowerCase();
  if (!type) return false;
  return ALLOWED_IMAGE_MIME_TYPES.has(type);
}

function formatBytesMb(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 MB";
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const out = { width: Number(img.naturalWidth) || 0, height: Number(img.naturalHeight) || 0 };
      URL.revokeObjectURL(objectUrl);
      resolve(out);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("IMAGE_DIMENSIONS_READ_ERROR"));
    };
    img.src = objectUrl;
  });
}

function paginateItems(items = [], currentPage = 1, pageSize = 6) {
  const list = Array.isArray(items) ? items : [];
  const size = Math.max(1, Number(pageSize) || 1);
  const totalItems = list.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / size));
  const page = Math.min(Math.max(1, Number(currentPage) || 1), totalPages);
  const startIndex = totalItems ? (page - 1) * size + 1 : 0;
  const endIndex = Math.min(totalItems, page * size);

  return {
    items: list.slice((page - 1) * size, page * size),
    totalItems,
    totalPages,
    currentPage: page,
    startIndex,
    endIndex,
  };
}

function buildPaginationTokens(totalPages, currentPage) {
  if (totalPages <= 1) return [1];
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);

  const tokens = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) tokens.push("ellipsis-left");
  for (let value = start; value <= end; value += 1) tokens.push(value);
  if (end < totalPages - 1) tokens.push("ellipsis-right");
  tokens.push(totalPages);

  return tokens;
}

function asArray(v) {
  return Array.isArray(v) ? v : [];
}

function asObject(v) {
  return v && typeof v === "object" ? v : {};
}

function extractItems(raw) {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== "object") return [];
  if (Array.isArray(raw.content)) return raw.content;
  if (Array.isArray(raw.items)) return raw.items;
  if (Array.isArray(raw.items?.content)) return raw.items.content;
  if (Array.isArray(raw.items?.items)) return raw.items.items;
  if (Array.isArray(raw.item?.items)) return raw.item.items;
  if (Array.isArray(raw.item?.content)) return raw.item.content;
  if (Array.isArray(raw.item?.items?.content)) return raw.item.items.content;
  if (Array.isArray(raw.item?.items?.items)) return raw.item.items.items;
  if (Array.isArray(raw.item)) return raw.item;
  return [];
}

function extractIdArray(raw) {
  const arr = Array.isArray(raw) ? raw : extractItems(raw);
  return arr
    .map((x) => (x && typeof x === "object" ? (x.id ?? x.userId ?? x.authorUserId ?? null) : x))
    .map((x) => Number(x))
    .filter((x) => Number.isFinite(x) && x > 0);
}

function extractPage(raw) {
  const obj = asObject(raw);
  if (Array.isArray(obj.content)) return obj;
  if (obj.items && typeof obj.items === "object" && Array.isArray(obj.items.content)) return obj.items;
  if (obj.item && typeof obj.item === "object" && Array.isArray(obj.item.content)) return obj.item;
  return { content: extractItems(raw), totalElements: extractItems(raw).length };
}

function parseDateValue(raw) {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

const RU_MONTH_TO_NUMBER = {
  января: "01",
  февраля: "02",
  марта: "03",
  апреля: "04",
  мая: "05",
  июня: "06",
  июля: "07",
  августа: "08",
  сентября: "09",
  октября: "10",
  ноября: "11",
  декабря: "12",
};

function normalizeLocalDateForApi(raw) {
  const value = cleanText(raw);
  if (!value) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const dotMatch = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dotMatch) {
    const day = dotMatch[1].padStart(2, "0");
    const month = dotMatch[2].padStart(2, "0");
    const year = dotMatch[3];
    return `${year}-${month}-${day}`;
  }

  const ruMatch = value.match(/^(\d{1,2})\s+([А-Яа-яЁё]+)\s+(\d{4})$/);
  if (ruMatch) {
    const day = ruMatch[1].padStart(2, "0");
    const month = RU_MONTH_TO_NUMBER[ruMatch[2].toLowerCase()];
    const year = ruMatch[3];
    if (month) return `${year}-${month}-${day}`;
  }

  return "";
}

function formatDate(raw) {
  const d = parseDateValue(raw);
  if (!d) return "—";
  return new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "long", year: "numeric" }).format(d);
}

function formatTime(raw) {
  const d = parseDateValue(raw);
  if (!d) return "—";
  return new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" }).format(d);
}

function formatDateTime(raw) {
  const d = parseDateValue(raw);
  if (!d) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function resolveLastLoginValue(me) {
  if (!me || typeof me !== "object") return null;
  const keys = [
    "lastLoginAt",
    "lastSeenAt",
    "last_login_at",
    "last_seen_at",
    "lastLogin",
    "lastSeen",
    "lastActivityAt",
    "last_activity_at",
  ];
  for (const key of keys) {
    const value = me[key];
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      return value;
    }
  }
  return null;
}

function formatPrice(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${new Intl.NumberFormat("ru-RU").format(n)} ₽`;
}

function monthToken(raw) {
  const d = parseDateValue(raw);
  if (!d) return "—";
  return new Intl.DateTimeFormat("ru-RU", { month: "short" }).format(d).replace(".", "");
}

function dayToken(raw) {
  const d = parseDateValue(raw);
  if (!d) return "—";
  return new Intl.DateTimeFormat("ru-RU", { day: "2-digit" }).format(d);
}

function normalizeChatPolicyValue(raw) {
  const policy = String(raw || "").trim().toUpperCase();
  return CHAT_POLICY_VALUES.has(policy) ? policy : CHAT_POLICY_ANY_AUTHORIZED;
}

function normalizeAppointment(item = {}) {
  const raw = asObject(item);
  const startAt = raw.startDateTime || raw.start_datetime || raw.startAt || raw.startsAt || raw.dateTime || raw.date || null;
  const endAt = raw.endDateTime || raw.end_datetime || raw.endAt || raw.endsAt || null;
  const title =
    raw.sessionTitle ||
    raw.groupSessionTitle ||
    raw.title ||
    raw.psychologistName ||
    raw.psychologist?.name ||
    raw.psychologistFullName ||
    raw.name ||
    "Сессия";
  const typeRaw = String(raw.type || raw.bookingType || raw.appointmentType || "").toUpperCase();
  const type = typeRaw.includes("GROUP") ? "GROUP" : "INDIVIDUAL";
  const status = String(raw.status || raw.bookingStatus || "").toUpperCase();
  const amount = raw.amount ?? raw.price ?? raw.totalAmount ?? raw.priceAmount ?? null;

  return {
    id: raw.id ?? raw.bookingId ?? raw.booking_id ?? raw.groupSessionId ?? null,
    title: String(title || "Сессия"),
    type,
    status,
    amount,
    startAt,
    endAt,
    isUpcoming: parseDateValue(startAt)?.getTime() > Date.now(),
  };
}

function normalizePayment(item = {}) {
  const raw = asObject(item);
  const statusRaw = String(raw.status || raw.paymentStatus || "").toUpperCase();
  let status = "pending";
  if (statusRaw.includes("PAID") || statusRaw.includes("SUCCESS") || statusRaw.includes("SUCCEEDED")) status = "success";
  if (statusRaw.includes("REFUND")) status = "refunded";
  return {
    id: raw.id ?? raw.paymentId ?? null,
    amount: raw.amount ?? raw.totalAmount ?? raw.price ?? null,
    description: String(raw.description || raw.title || raw.sessionTitle || "Платёж"),
    createdAt: raw.createdAt || raw.createdWhen || raw.paidAt || null,
    status,
    statusLabel: status === "success" ? "Оплачено" : status === "refunded" ? "Возврат" : "В обработке",
  };
}

function normalizePersonCard(item = {}, defaultTitle = "Пользователь") {
  const raw = asObject(item);
  const id = raw.id ?? raw.userId ?? raw.authorUserId ?? null;
  const name = raw.name || raw.fullName || raw.title || raw.login || (id ? `#${id}` : defaultTitle);
  const subtitle = raw.headline || raw.about || raw.specialization || raw.description || "";
  return {
    id,
    name: String(name || defaultTitle),
    subtitle: String(subtitle || ""),
  };
}

function normalizeSuggestedPodcast(item = {}) {
  const raw = asObject(item);
  const source = asObject(raw.podcast || raw.item || raw.show || raw);
  const author = asObject(raw.author);
  const routeId = source.slug || raw.slug || source.id || raw.id || raw.podcastId || null;
  return {
    id: source.id ?? raw.id ?? raw.podcastId ?? null,
    routeId,
    title: String(source.title || source.name || raw.title || raw.name || "Подкаст"),
    authorName: String(
      asObject(source.author).name ||
        author.name ||
        source.authorName ||
        source.psychologistName ||
        raw.authorName ||
        raw.psychologistName ||
        "Специалист"
    ),
    episodesCount: Number(source.episodesCount || source.episodes || raw.episodesCount || raw.episodes || 0),
    badge: String(source.coverEmoji || raw.coverEmoji || "🎙️"),
  };
}

function normalizeSuggestedBook(item = {}) {
  const raw = asObject(item);
  const source = asObject(raw.book || raw.item || raw.literatureBook || raw);
  const routeId = source.slug || raw.slug || source.id || raw.id || raw.bookId || null;
  return {
    id: source.id ?? raw.id ?? raw.bookId ?? null,
    routeId,
    title: String(source.title || source.name || raw.title || raw.name || "Книга"),
    author: String(
      source.author ||
        source.authorName ||
        source.writer ||
        raw.author ||
        raw.authorName ||
        raw.writer ||
        "Автор не указан"
    ),
    badge: "📘",
  };
}

function roleLabel(role) {
  const r = String(role || "").replace(/^ROLE_/, "").toUpperCase();
  if (r === "ADMIN") return "Администратор";
  if (r === "PSYCHOLOGIST") return "Психолог";
  if (r === "MODERATOR") return "Модератор";
  return "Клиент";
}

function contentCategoryLabel(category) {
  const normalized = String(category || "").trim().toLowerCase();
  if (normalized === "blog") return "Блог";
  if (normalized === "forum") return "Форум";
  if (normalized === "podcast") return "Подкасты";
  if (normalized === "literature") return "Литература";
  return "Контент";
}

function parsePositiveId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parseBlogPostRefFromUrl(rawUrl = "") {
  const value = String(rawUrl || "").trim();
  if (!value) return "";
  const match = value.match(/\/blog\/([^?#/]+)/i);
  return String(match?.[1] || "").trim();
}

function parseForumTopicIdFromUrl(rawUrl = "") {
  const value = String(rawUrl || "").trim();
  if (!value) return null;
  const match = value.match(/\/forum\/topics\/([^?#/]+)/i);
  const token = String(match?.[1] || "").trim();
  if (!token) return null;
  const numeric = token.match(/^(\d+)/);
  return parsePositiveId(numeric ? numeric[1] : token);
}

function parsePodcastEpisodeIdFromUrl(rawUrl = "") {
  const value = String(rawUrl || "").trim();
  if (!value) return null;
  const match = value.match(/\/podcasts\/episodes\/([^?#/]+)/i);
  const token = String(match?.[1] || "").trim();
  if (!token) return null;
  const numeric = token.match(/^(\d+)/);
  return parsePositiveId(numeric ? numeric[1] : token);
}

function parseLiteratureBookRefFromUrl(rawUrl = "") {
  const value = String(rawUrl || "").trim();
  if (!value) return "";
  const match = value.match(/\/literature\/books\/([^?#/]+)/i);
  return String(match?.[1] || "").trim();
}

function buildBlogPostPath(post) {
  if (!post || typeof post !== "object") return "/blog";
  const id = parsePositiveId(post.id ?? post.postId ?? post.blogPostId ?? post.publicationId ?? post.post?.id);
  const slug = String(post.slug || post.postSlug || post.post?.slug || "").trim();
  if (id && slug) return `/blog/${id}-${slug}`;
  if (slug) return `/blog/${slug}`;
  if (id) return `/blog/${id}`;
  return "/blog";
}

function resolveForumTopicPath(topic = {}) {
  const raw = asObject(topic);
  const topicId = parsePositiveId(raw.id ?? raw.topicId ?? raw.topic?.id ?? parseForumTopicIdFromUrl(raw.linkUrl || raw.url || ""));
  return topicId ? `/forum/topics/${topicId}` : "/forum";
}

function resolveCommentSourceTitle(comment = {}) {
  const raw = asObject(comment);
  const topic = asObject(raw.topic);
  const post = asObject(raw.post);
  const episode = asObject(raw.episode);
  const book = asObject(raw.book);
  return String(
    raw.topicTitle ||
      raw.postTitle ||
      raw.blogPostTitle ||
      raw.episodeTitle ||
      raw.podcastEpisodeTitle ||
      raw.bookTitle ||
      raw.literatureBookTitle ||
      topic.title ||
      post.title ||
      episode.title ||
      book.title ||
      ""
  ).trim();
}

function resolveCommentTargetPath(comment = {}) {
  const raw = asObject(comment);
  const category = String(raw.category || "").trim().toLowerCase();
  const linkUrl = String(raw.linkUrl || raw.url || raw.targetUrl || raw.link || "").trim();

  if (category === "forum") {
    const topicId = parsePositiveId(raw.topicId ?? raw.topic?.id ?? parseForumTopicIdFromUrl(linkUrl));
    return topicId ? `/forum/topics/${topicId}` : "/forum/my/comments";
  }

  if (category === "blog") {
    const postPath = buildBlogPostPath({
      id: raw.postId ?? raw.blogPostId ?? raw.post?.id,
      slug: raw.postSlug ?? raw.slug ?? raw.post?.slug,
    });
    if (postPath !== "/blog") return `${postPath}#comments`;
    const postRef = parseBlogPostRefFromUrl(linkUrl);
    if (postRef) return `/blog/${postRef}#comments`;
    return "/blog";
  }

  if (category === "podcast") {
    const episodeId = parsePositiveId(raw.episodeId ?? raw.podcastEpisodeId ?? raw.episode?.id ?? parsePodcastEpisodeIdFromUrl(linkUrl));
    return episodeId ? `/podcasts/episodes/${episodeId}` : "/podcasts";
  }

  if (category === "literature") {
    const bookRef = String(
      raw.bookId ??
        raw.bookSlug ??
        raw.literatureBookId ??
        raw.book?.id ??
        raw.book?.slug ??
        parseLiteratureBookRefFromUrl(linkUrl)
    ).trim();
    return bookRef ? `/literature/books/${encodeURIComponent(bookRef)}#book-reviews` : "/literature";
  }

  if (linkUrl.startsWith("/")) return linkUrl;
  return "/dashboard/settings?tab=content";
}

function contentOpenLabel(category) {
  const normalized = String(category || "").trim().toLowerCase();
  if (normalized === "forum") return "К теме";
  if (normalized === "blog") return "К посту";
  if (normalized === "podcast") return "К эпизоду";
  if (normalized === "literature") return "К книге";
  return "Открыть";
}

function activeBadgeClass(status) {
  if (status === "success") return "success";
  if (status === "refunded") return "refunded";
  return "pending";
}

function profileInitials(name, login) {
  const byName = String(name || "").trim().split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0]).join("");
  if (byName) return byName.toUpperCase();
  const byLogin = String(login || "").trim().slice(0, 2);
  return byLogin ? byLogin.toUpperCase() : "U";
}

function buildProfileTabs({ isPsychologist = false, canManageIntegrations = false, isClient = false } = {}) {
  const hiddenTabs = new Set();
  if (isClient) {
    hiddenTabs.add("subscriptions");
    hiddenTabs.add("integrations");
  } else if (!canManageIntegrations) {
    hiddenTabs.add("integrations");
  }

  const base = BASE_PROFILE_TABS.filter((tab) => !hiddenTabs.has(tab.id));
  return isPsychologist ? [...base, PSYCHOLOGIST_PROFILE_TAB] : base;
}

function resolveProfileTab(tabFromQuery, availableTabs = BASE_PROFILE_TABS, fallback = "profile") {
  const normalized = String(tabFromQuery || "").trim().toLowerCase();
  const known = new Set(
    asArray(availableTabs)
      .map((tab) => String(tab?.id || "").trim().toLowerCase())
      .filter(Boolean)
  );
  if (known.has(normalized)) return normalized;
  return fallback;
}

function cleanText(value) {
  return String(value || "").trim();
}

function normalizeDictOptions(raw, prefix = "dict") {
  const source = extractItems(raw);
  const out = [];
  const seen = new Set();

  source.forEach((item, index) => {
    const row = asObject(item);
    const title = cleanText(row.title || row.name || row.label || row.code);
    const numericId = parsePositiveId(
      row.id ??
        row.value ??
        row.dictId ??
        row.themeId ??
        row.methodId
    );
    const fallbackId = cleanText(row.id ?? row.value);
    const id = (numericId ?? fallbackId) || `${prefix}-${index + 1}`;
    if (!title) return;
    const key = String(id);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      id,
      title,
      code: cleanText(row.code),
      isActive: row.isActive !== false && row.active !== false,
    });
  });

  return out;
}

function findOptionByTitle(options = [], value = "") {
  const needle = cleanText(value).toLowerCase();
  if (!needle) return null;
  return (
    asArray(options).find((option) => {
      const title = cleanText(option?.title).toLowerCase();
      const code = cleanText(option?.code).toLowerCase();
      return title === needle || code === needle;
    }) || null
  );
}

function makeVirtualDictId(prefix, value, index = 0) {
  const normalized = cleanText(value)
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9\u0400-\u04ff-]+/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return `${prefix}-${normalized || index + 1}`;
}

function resolveDictSelection(rawValues, rawIds, options = [], prefix = "dict") {
  const selected = [];
  const byId = new Map(asArray(options).map((option) => [String(option.id), option]));
  const seen = new Set();

  const addOption = (option) => {
    if (!option) return;
    const key = String(option.id);
    if (!key || seen.has(key)) return;
    seen.add(key);
    selected.push(option);
  };

  const addById = (idValue) => {
    const numericId = parsePositiveId(idValue);
    const key = String(numericId ?? cleanText(idValue));
    if (!key) return;
    if (byId.has(key)) {
      addOption(byId.get(key));
      return;
    }
    addOption({ id: key, title: `#${key}`, code: "", isActive: true });
  };

  asArray(rawIds).forEach((idValue) => addById(idValue));

  asArray(rawValues).forEach((item, index) => {
    if (item && typeof item === "object") {
      const row = asObject(item);
      const idValue = row.id ?? row.value ?? row.dictId ?? row.themeId ?? row.methodId;
      const titleValue = cleanText(row.title || row.name || row.label || row.code);
      if (idValue != null && cleanText(idValue)) {
        addById(idValue);
        return;
      }
      if (!titleValue) return;
      const matched = findOptionByTitle(options, titleValue);
      if (matched) {
        addOption(matched);
        return;
      }
      addOption({
        id: makeVirtualDictId(prefix, titleValue, index),
        title: titleValue,
        code: "",
        isActive: true,
      });
      return;
    }

    const scalar = cleanText(item);
    if (!scalar) return;
    const numericId = parsePositiveId(scalar);
    if (numericId) {
      addById(numericId);
      return;
    }
    const matched = findOptionByTitle(options, scalar);
    if (matched) {
      addOption(matched);
      return;
    }
    addOption({
      id: makeVirtualDictId(prefix, scalar, index),
      title: scalar,
      code: "",
      isActive: true,
    });
  });

  return selected;
}

function resolveContactType(rawType, rawLabel, rawValue) {
  const type = cleanText(rawType || rawLabel).toLowerCase();
  const value = cleanText(rawValue).toLowerCase();
  if (type.includes("mail") || value.includes("@")) return "email";
  if (type.includes("phone") || type.includes("tel") || /^(\+?\d[\d\s\-()]{5,})$/.test(value)) return "phone";
  if (type.includes("telegram") || type === "tg" || value.includes("t.me/")) return "telegram";
  if (type.includes("site") || type.includes("web") || type.includes("url")) return "website";
  if (type.includes("region") || type.includes("location") || type.includes("city") || type.includes("address")) return "location";
  return "other";
}

function resolveSocialType(rawType, rawLabel, rawValue) {
  const type = cleanText(rawType || rawLabel).toLowerCase();
  const value = cleanText(rawValue).toLowerCase();
  if (!type && !value) return "";
  if (type.includes("telegram") || type === "tg" || value.includes("t.me/")) return "telegram";
  if (type.includes("instagram") || value.includes("instagram.com/")) return "instagram";
  if (type.includes("vk") || type.includes("vkontakte") || value.includes("vk.com/")) return "vk";
  if (type.includes("whatsapp") || value.includes("wa.me/") || value.includes("whatsapp.com/")) return "whatsapp";
  if (type.includes("youtube") || value.includes("youtube.com/") || value.includes("youtu.be/")) return "youtube";
  if (type.includes("tiktok") || value.includes("tiktok.com/")) return "tiktok";
  if (type.includes("facebook") || value.includes("facebook.com/")) return "facebook";
  if (type.includes("linkedin") || value.includes("linkedin.com/")) return "linkedin";
  if (type.includes("site") || type.includes("web") || type.includes("url")) return "website";
  if (/^https?:\/\//.test(value) || /^www\./.test(value)) return "website";
  return "";
}

function socialOptionByType(type) {
  return SOCIAL_NETWORK_OPTIONS.find((item) => item.type === type) || SOCIAL_NETWORK_OPTIONS[0];
}

function createSocialRow(source = {}, index = 0) {
  const row = asObject(source);
  const inferredType = resolveSocialType(
    row.type,
    row.label,
    row.url || row.value || row.link || row.href
  );
  const type = cleanText(row.type || inferredType) || "telegram";
  const option = socialOptionByType(type);
  return {
    id: row.id ?? `social-${index + 1}`,
    type,
    url: cleanText(row.url || row.value || row.link || row.href),
    label: cleanText(row.label) || option.label,
  };
}

function normalizeSocialRows(rows) {
  const unique = new Set();
  return asArray(rows)
    .map((row, index) => createSocialRow(row, index))
    .filter((row) => row.type && row.url)
    .filter((row) => {
      const key = `${row.type}::${row.url.toLowerCase()}`;
      if (unique.has(key)) return false;
      unique.add(key);
      return true;
    });
}

function createEducationRow(source = {}, index = 0) {
  const row = asObject(source);
  return {
    id: row.id ?? `edu-${index + 1}`,
    institution: cleanText(row.institution || row.title || row.name || row.program),
    specialization: cleanText(row.specialization || row.degree || row.sub || row.issuer),
    fileUrl: cleanText(row.fileUrl || row.documentUrl || row.attachmentUrl),
    fileName: cleanText(row.fileName || row.originalFileName || row.attachmentName),
  };
}

function createCertificateRow(source = {}, index = 0) {
  const row = asObject(source);
  return {
    id: row.id ?? `cert-${index + 1}`,
    title: cleanText(row.title || row.name || row.certificateName),
    issuer: cleanText(row.issuer || row.organization || row.issuedBy),
    issuedAt: cleanText(row.issuedAt || row.issuedYear || row.year || row.date),
    documentUrl: cleanText(row.documentUrl || row.fileUrl || row.url),
    fileName: cleanText(row.fileName || row.originalFileName),
    mimeType: cleanText(
      row.mimeType || row.mime_type || row.contentType || row.content_type || row.fileMimeType || row.file_mime_type
    ),
    verified: Boolean(row.verified || row.isVerified),
    verificationStatus: row.verificationStatus || row.status || "PENDING",
  };
}

function createEmptyPsychologistSettingsForm() {
  return {
    themes: [],
    methods: [],
    experienceYears: "",
    pricePerSession: "",
    about: "",
    education: [],
    certificates: [],
    publicEmail: "",
    contactPhone: "",
    socialLinks: [],
    websiteUrl: "",
    telegramUrl: "",
    regionLabel: "",
    workFormatOnline: true,
    workFormatOffline: false,
  };
}

function normalizePsychologistSettingsForm(rawProfile, themeOptions = [], methodOptions = []) {
  const profile = asObject(rawProfile);
  const contactPoints = asArray(profile.contactPoints);
  const resolveContactValue = (kind) => {
    const match = contactPoints.find((item) => {
      const row = asObject(item);
      const type = resolveContactType(row.type, row.label, row.value || row.url);
      return type === kind;
    });
    if (!match) return "";
    return cleanText(match.value || match.url);
  };
  const socialFromContactPoints = contactPoints
    .map((item, index) => createSocialRow(item, index))
    .filter((row) => Boolean(resolveSocialType(row.type, row.label, row.url)));
  const socialFromLegacyFields = [
    profile.telegramUrl ? { type: "telegram", url: cleanText(profile.telegramUrl) } : null,
    profile.instagramUrl ? { type: "instagram", url: cleanText(profile.instagramUrl) } : null,
    profile.whatsappUrl ? { type: "whatsapp", url: cleanText(profile.whatsappUrl) } : null,
    profile.websiteUrl || profile.siteUrl
      ? { type: "website", url: cleanText(profile.websiteUrl || profile.siteUrl) }
      : null,
  ].filter(Boolean);
  const socialLinks = normalizeSocialRows([
    ...socialFromContactPoints,
    ...socialFromLegacyFields,
  ]);
  const websiteFromSocial = socialLinks.find((item) => item.type === "website")?.url || "";
  const telegramFromSocial = socialLinks.find((item) => item.type === "telegram")?.url || "";

  const themeSelection = resolveDictSelection(profile.themes, profile.themeIds ?? profile.theme_ids, themeOptions, "theme");
  const methodSelection = resolveDictSelection(profile.methods, profile.methodIds ?? profile.method_ids, methodOptions, "method");

  const experienceYears = Number(profile.experienceYears ?? profile.expYears ?? profile.experience);
  const pricePerSession = Number(profile.pricePerSession ?? profile.price);
  const formatsRaw = asArray(profile.formats)
    .map((item) => cleanText(typeof item === "string" ? item : item?.title || item?.code))
    .map((item) => item.toLowerCase());
  const hasOnlineInFormats = formatsRaw.some((item) => item.includes("online") || item.includes("онлайн"));
  const hasOfflineInFormats = formatsRaw.some((item) => item.includes("offline") || item.includes("оффлайн"));

  return {
    themes: themeSelection,
    methods: methodSelection,
    experienceYears:
      Number.isFinite(experienceYears) && experienceYears >= 0 ? String(Math.round(experienceYears)) : "",
    pricePerSession:
      Number.isFinite(pricePerSession) && pricePerSession >= 0 ? String(Math.round(pricePerSession)) : "",
    about: cleanText(profile.about || profile.description || profile.headline),
    education: asArray(profile.education).map(createEducationRow),
    certificates: asArray(profile.certificates).map(createCertificateRow),
    socialLinks,
    publicEmail: cleanText(profile.publicEmail) || resolveContactValue("email"),
    contactPhone: cleanText(profile.contactPhone) || resolveContactValue("phone"),
    websiteUrl:
      cleanText(profile.websiteUrl || profile.siteUrl) ||
      websiteFromSocial ||
      resolveContactValue("website"),
    telegramUrl:
      cleanText(profile.telegramUrl) || telegramFromSocial || resolveContactValue("telegram"),
    regionLabel: cleanText(profile.regionLabel || profile.region) || resolveContactValue("location"),
    workFormatOnline:
      profile.workFormatOnline == null
        ? hasOnlineInFormats || (!hasOfflineInFormats && Boolean(profile.workFormatOffline) === false)
        : Boolean(profile.workFormatOnline),
    workFormatOffline:
      profile.workFormatOffline == null ? hasOfflineInFormats : Boolean(profile.workFormatOffline),
  };
}

function buildPsychologistProfilePayload(sourceProfile = {}, form = {}) {
  const profile = asObject(sourceProfile);
  const next = { ...profile };
  // Visibility is controlled by backend auto-publish rules.
  // Do not pass stale visibility flags from old raw payload.
  delete next.isVisible;
  delete next.is_visible;
  delete next.visible;
  const selectedThemes = asArray(form.themes).filter(Boolean);
  const selectedMethods = asArray(form.methods).filter(Boolean);

  const themeIds = selectedThemes
    .map((item) => parsePositiveId(item?.id ?? item))
    .filter(Boolean);
  const methodIds = selectedMethods
    .map((item) => parsePositiveId(item?.id ?? item))
    .filter(Boolean);
  const themeTitles = selectedThemes
    .map((item) => cleanText(item?.title || item?.name || item))
    .filter(Boolean);
  const methodTitles = selectedMethods
    .map((item) => cleanText(item?.title || item?.name || item))
    .filter(Boolean);

  const experienceYears = Number(cleanText(form.experienceYears));
  const pricePerSession = Number(cleanText(form.pricePerSession));

  const education = asArray(form.education)
    .map((row) => ({
      id: parsePositiveId(row?.id ?? null) || undefined,
      title: cleanText(row?.institution),
      institution: cleanText(row?.institution),
      specialization: cleanText(row?.specialization),
      fileUrl: cleanText(row?.fileUrl),
      fileName: cleanText(row?.fileName),
      verified: Boolean(row?.verified),
    }))
    .filter((row) => row.title || row.specialization);

  const certificates = asArray(form.certificates)
    .map((row) => ({
      id: parsePositiveId(row?.id ?? null) || undefined,
      title: cleanText(row?.title),
      issuer: cleanText(row?.issuer),
      issuedAt: normalizeLocalDateForApi(row?.issuedAt) || undefined,
      documentUrl: cleanText(row?.documentUrl),
      fileName: cleanText(row?.fileName),
      verified: Boolean(row?.verified),
      verificationStatus: cleanText(row?.verificationStatus || row?.status),
    }))
    .filter((row) => row.title || row.documentUrl || row.issuer);

  const socialLinks = normalizeSocialRows(form.socialLinks);
  const socialContacts = socialLinks
    .map((item) => {
      const option = socialOptionByType(item.type);
      const url = cleanText(item.url);
      if (!url) return null;
      return {
        type: item.type,
        label: option.label,
        value: url,
        url,
      };
    })
    .filter(Boolean);

  const contacts = [
    form.publicEmail
      ? { type: "email", label: "Email", value: cleanText(form.publicEmail), url: `mailto:${cleanText(form.publicEmail)}` }
      : null,
    form.contactPhone
      ? { type: "phone", label: "Телефон", value: cleanText(form.contactPhone), url: `tel:${cleanText(form.contactPhone)}` }
      : null,
    form.regionLabel
      ? { type: "location", label: "Регион", value: cleanText(form.regionLabel), url: "" }
      : null,
    ...socialContacts,
  ].filter(Boolean);

  next.themeIds = themeIds;
  next.methodIds = methodIds;
  next.themes = themeTitles;
  next.methods = methodTitles;
  next.experienceYears =
    Number.isFinite(experienceYears) && experienceYears >= 0 ? Math.round(experienceYears) : 0;
  next.pricePerSession =
    Number.isFinite(pricePerSession) && pricePerSession >= 0 ? Math.round(pricePerSession) : null;
  next.about = cleanText(form.about);
  next.description = cleanText(form.about);
  next.education = education;
  next.certificates = certificates;
  next.websiteUrl = socialLinks.find((item) => item.type === "website")?.url || "";
  next.telegramUrl = socialLinks.find((item) => item.type === "telegram")?.url || "";
  next.instagramUrl = socialLinks.find((item) => item.type === "instagram")?.url || "";
  next.whatsappUrl = socialLinks.find((item) => item.type === "whatsapp")?.url || "";
  next.workFormatOnline = Boolean(form.workFormatOnline);
  next.workFormatOffline = Boolean(form.workFormatOffline);
  next.publicEmail = cleanText(form.publicEmail);
  next.contactPhone = cleanText(form.contactPhone);
  next.regionLabel = cleanText(form.regionLabel);
  next.contactPoints = contacts;
  return next;
}

function normalizePsychologistIdList(items) {
  return asArray(items)
    .map((item) => String(item?.id ?? item ?? "").trim())
    .filter(Boolean)
    .sort();
}

function normalizePsychologistEducationRows(rows) {
  return asArray(rows)
    .map((row) => ({
      institution: cleanText(row?.institution),
      specialization: cleanText(row?.specialization),
      fileUrl: cleanText(row?.fileUrl),
      fileName: cleanText(row?.fileName),
    }))
    .filter((row) => row.institution || row.specialization || row.fileUrl || row.fileName);
}

function normalizePsychologistCertificateRows(rows) {
  return asArray(rows)
    .map((row) => ({
      title: cleanText(row?.title),
      issuer: cleanText(row?.issuer),
      issuedAt: cleanText(row?.issuedAt),
      documentUrl: cleanText(row?.documentUrl),
      fileName: cleanText(row?.fileName),
      verified: Boolean(row?.verified),
      verificationStatus: cleanText(row?.verificationStatus || row?.status),
    }))
    .filter(
      (row) =>
        row.title ||
        row.issuer ||
        row.issuedAt ||
        row.documentUrl ||
        row.fileName ||
        row.verified ||
        row.verificationStatus
    );
}

function buildPsychologistSectionSnapshot(form = {}) {
  const source = asObject(form);
  return {
    profile: {
      themes: normalizePsychologistIdList(source.themes),
      methods: normalizePsychologistIdList(source.methods),
    },
    work: {
      experienceYears: cleanText(source.experienceYears),
      pricePerSession: cleanText(source.pricePerSession),
      about: cleanText(source.about),
      workFormatOnline: Boolean(source.workFormatOnline),
      workFormatOffline: Boolean(source.workFormatOffline),
    },
    educationContacts: {
      education: normalizePsychologistEducationRows(source.education),
      publicEmail: cleanText(source.publicEmail),
      contactPhone: cleanText(source.contactPhone),
      socialLinks: normalizeSocialRows(source.socialLinks),
      regionLabel: cleanText(source.regionLabel),
    },
    certificates: normalizePsychologistCertificateRows(source.certificates),
  };
}

function areEqualSectionSnapshots(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function Settings() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { me, boot, logout } = useAuth();
  const { unreadNotificationsCount = 0 } = useNotifications();
  const normalizedRole = String(me?.role || "").replace(/^ROLE_/, "").toUpperCase();
  const isClient = normalizedRole === "CLIENT";
  const isPsychologist = normalizedRole === "PSYCHOLOGIST";
  const canManageIntegrations = Boolean(normalizedRole) && normalizedRole !== "CLIENT";
  const canManageQuasarIntegration = canManageIntegrations && QUASAR_INTEGRATION_ENABLED;
  const profileTabs = React.useMemo(
    () => buildProfileTabs({ isPsychologist, canManageIntegrations, isClient }),
    [isPsychologist, canManageIntegrations, isClient]
  );
  const [activeTab, setActiveTab] = React.useState(() =>
    resolveProfileTab(
      searchParams.get("tab"),
      buildProfileTabs({ isPsychologist, canManageIntegrations, isClient })
    )
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);

  const [fullName, setFullName] = React.useState("");
  const [login, setLogin] = React.useState("");
  const [avatarUrl, setAvatarUrl] = React.useState("");
  const [savingProfile, setSavingProfile] = React.useState(false);
  const [uploadingAvatar, setUploadingAvatar] = React.useState(false);
  const [uploadingCover, setUploadingCover] = React.useState(false);

  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmNewPassword, setConfirmNewPassword] = React.useState("");
  const [changingPassword, setChangingPassword] = React.useState(false);

  const [theme, setTheme] = React.useState(() => readThemeFromStorage());
  const [themeSaving, setThemeSaving] = React.useState(false);
  const [restartingOnboarding, setRestartingOnboarding] = React.useState(false);

  const [notificationSettings, setNotificationSettings] = React.useState({});
  const [savingNotificationKey, setSavingNotificationKey] = React.useState("");
  const [psychologistChatPolicy, setPsychologistChatPolicy] = React.useState(
    CHAT_POLICY_ANY_AUTHORIZED
  );
  const [loadingPsychologistChatPolicy, setLoadingPsychologistChatPolicy] = React.useState(false);
  const [savingPsychologistChatPolicy, setSavingPsychologistChatPolicy] = React.useState(false);

  const [appointments, setAppointments] = React.useState([]);
  const [payments, setPayments] = React.useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = React.useState(true);

  const [psychSubscriptions, setPsychSubscriptions] = React.useState([]);
  const [favorites, setFavorites] = React.useState([]);
  const [blogAuthors, setBlogAuthors] = React.useState([]);
  const [podcastSubscriptions, setPodcastSubscriptions] = React.useState([]);
  const [savedBooks, setSavedBooks] = React.useState([]);
  const [featuredPodcasts, setFeaturedPodcasts] = React.useState([]);
  const [featuredBooks, setFeaturedBooks] = React.useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = React.useState(true);
  const [unsubscribingPsychId, setUnsubscribingPsychId] = React.useState("");
  const [removingFavoriteId, setRemovingFavoriteId] = React.useState("");
  const [unsubscribingBlogAuthorId, setUnsubscribingBlogAuthorId] = React.useState("");
  const [subscribingPodcastId, setSubscribingPodcastId] = React.useState("");
  const [unsubscribingPodcastId, setUnsubscribingPodcastId] = React.useState("");
  const [savingBookId, setSavingBookId] = React.useState("");
  const [unsavingBookId, setUnsavingBookId] = React.useState("");

  const [forumTopics, setForumTopics] = React.useState([]);
  const [blogPosts, setBlogPosts] = React.useState([]);
  const [comments, setComments] = React.useState([]);
  const [commentFilter, setCommentFilter] = React.useState("all");
  const [contentLoading, setContentLoading] = React.useState(true);
  const [testsAssignedCount, setTestsAssignedCount] = React.useState(null);
  const [diaryEntriesCount, setDiaryEntriesCount] = React.useState(null);
  const [listPages, setListPages] = React.useState(() => createInitialPageState());

  const [pushDevices, setPushDevices] = React.useState([]);
  const [pushDevicesLoading, setPushDevicesLoading] = React.useState(true);
  const [disconnectingDevice, setDisconnectingDevice] = React.useState("");
  const [quasarLoading, setQuasarLoading] = React.useState(false);
  const [quasarActionLoading, setQuasarActionLoading] = React.useState(false);
  const [quasar, setQuasar] = React.useState(null);
  const [googleCalendarLoading, setGoogleCalendarLoading] = React.useState(false);
  const [googleCalendarActionLoading, setGoogleCalendarActionLoading] = React.useState(false);
  const [googleCalendarSyncing, setGoogleCalendarSyncing] = React.useState(false);
  const [googleCalendarStatus, setGoogleCalendarStatus] = React.useState(null);
  const [googleCalendarConsentChecked, setGoogleCalendarConsentChecked] = React.useState(false);
  const [googleSyncConfiguratorOpen, setGoogleSyncConfiguratorOpen] = React.useState(false);
  const [googleSyncOptions, setGoogleSyncOptions] = React.useState({
    direction: "BIDIRECTIONAL",
    conflictPolicy: "SKIP_IF_BOOKED",
    daysBack: 7,
    daysForward: 180,
    exportCalendarId: "primary",
    busyCalendarIds: [],
  });
  const [yandexCalendarLoading, setYandexCalendarLoading] = React.useState(false);
  const [yandexCalendarActionLoading, setYandexCalendarActionLoading] = React.useState(false);
  const [yandexCalendarSyncing, setYandexCalendarSyncing] = React.useState(false);
  const [yandexCalendarStatus, setYandexCalendarStatus] = React.useState(null);
  const [yandexCalendarConsentChecked, setYandexCalendarConsentChecked] = React.useState(false);
  const [yandexSyncConfiguratorOpen, setYandexSyncConfiguratorOpen] = React.useState(false);
  const [yandexSyncOptions, setYandexSyncOptions] = React.useState({
    direction: "BOTH",
    conflictPolicy: "SKIP_IF_BOOKED",
    daysBack: 7,
    daysForward: 180,
    exportCalendarUrl: "",
    busyCalendarUrls: [],
  });

  const [psychologistSettingsLoading, setPsychologistSettingsLoading] = React.useState(false);
  const [psychologistSettingsSaving, setPsychologistSettingsSaving] = React.useState(false);
  const [psychologistProfileRaw, setPsychologistProfileRaw] = React.useState({});
  const [psychologistThemeOptions, setPsychologistThemeOptions] = React.useState([]);
  const [psychologistMethodOptions, setPsychologistMethodOptions] = React.useState([]);
  const [psychologistSettingsForm, setPsychologistSettingsForm] = React.useState(
    () => createEmptyPsychologistSettingsForm()
  );
  const [psychologistSettingsBaseline, setPsychologistSettingsBaseline] = React.useState(
    () => createEmptyPsychologistSettingsForm()
  );
  const [activeCertificatePreview, setActiveCertificatePreview] = React.useState(null);
  const [uploadingEducationFile, setUploadingEducationFile] = React.useState(null);
  const [uploadingCertificateFile, setUploadingCertificateFile] = React.useState(null);

  const fileInputRef = React.useRef(null);
  const coverFileInputRef = React.useRef(null);

  React.useEffect(() => {
    if (!me) return;
    setFullName(sanitizeFullNameInput(me.name || ""));
    setLogin(String(me.login || ""));
    setAvatarUrl(String(me.avatarUrl || ""));
  }, [me]);

  const applyTheme = React.useCallback((nextTheme) => {
    applyThemeToDocument(nextTheme);
  }, []);

  React.useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  const loadThemeSettings = React.useCallback(async () => {
    try {
      const res = await profileApi.getUiSettings();
      const nextTheme = String(res?.theme || "").toLowerCase() === "dark" ? "dark" : "light";
      setTheme(nextTheme);
      persistTheme(nextTheme);
    } catch {
      const fallbackTheme = readThemeFromStorage();
      setTheme(fallbackTheme);
      persistTheme(fallbackTheme);
    }
  }, []);

  const loadNotificationSettings = React.useCallback(async () => {
    try {
      const res = await notificationsApi.getSettings();
      setNotificationSettings(asObject(res));
    } catch {
      setNotificationSettings({});
    }
  }, []);

  const loadPsychologistChatPolicy = React.useCallback(async () => {
    if (!isPsychologist) {
      setPsychologistChatPolicy(CHAT_POLICY_ANY_AUTHORIZED);
      return;
    }

    setLoadingPsychologistChatPolicy(true);
    try {
      const res = await profileApi.getMyPsychologistChatSettings();
      const next = normalizeChatPolicyValue(
        res?.inboundPolicy ?? res?.directMessagesPolicy
      );
      setPsychologistChatPolicy(next);
    } catch (err) {
      const code = String(err?.code || "").trim().toUpperCase();
      if (code !== "ONLY_PSYCHOLOGIST") {
        toast.error(err?.message || "Не удалось загрузить настройки входящих сообщений");
      }
      setPsychologistChatPolicy(CHAT_POLICY_ANY_AUTHORIZED);
    } finally {
      setLoadingPsychologistChatPolicy(false);
    }
  }, [isPsychologist, toast]);

  const loadPushDevices = React.useCallback(async () => {
    setPushDevicesLoading(true);
    try {
      const res = await pwaApi.listPushDevices();
      setPushDevices(asArray(res?.items));
    } catch {
      setPushDevices([]);
    } finally {
      setPushDevicesLoading(false);
    }
  }, []);

  const loadQuasarSettings = React.useCallback(async () => {
    if (!canManageQuasarIntegration) {
      setQuasar(null);
      return;
    }
    setQuasarLoading(true);
    try {
      const res = await profileApi.getMyQuasarSettings();
      setQuasar(res || null);
    } catch (err) {
      setQuasar(null);
      toast.error(err?.message || "Не удалось загрузить Quasar");
    } finally {
      setQuasarLoading(false);
    }
  }, [canManageQuasarIntegration, toast]);

  const loadGoogleCalendarStatus = React.useCallback(async () => {
    if (!canManageIntegrations) {
      setGoogleCalendarStatus(null);
      return;
    }
    setGoogleCalendarLoading(true);
    try {
      const res = await profileApi.getMyGoogleCalendarStatus();
      setGoogleCalendarStatus(res || null);
      setGoogleSyncOptions({
        direction: String(res?.syncDirection || "BIDIRECTIONAL"),
        conflictPolicy: String(res?.conflictPolicy || "SKIP_IF_BOOKED"),
        daysBack: Math.max(0, Math.min(60, Number(res?.daysBack) || 7)),
        daysForward: Math.max(1, Math.min(365, Number(res?.daysForward) || 180)),
        exportCalendarId: String(res?.exportCalendarId || res?.calendarId || "primary"),
        busyCalendarIds: Array.isArray(res?.busyCalendarIds) ? res.busyCalendarIds.map((item) => String(item || "")).filter(Boolean) : [],
      });
      if (!res?.connected) {
        setGoogleSyncConfiguratorOpen(false);
      } else {
        setGoogleCalendarConsentChecked(false);
      }
    } catch (err) {
      setGoogleCalendarStatus(null);
      toast.error(resolveCalendarErrorMessage(err, { provider: "Google Calendar", fallback: "Не удалось загрузить Google Calendar" }));
    } finally {
      setGoogleCalendarLoading(false);
    }
  }, [canManageIntegrations, toast]);

  const loadYandexCalendarStatus = React.useCallback(async () => {
    if (!canManageIntegrations) {
      setYandexCalendarStatus(null);
      return;
    }
    setYandexCalendarLoading(true);
    try {
      const res = await profileApi.getMyYandexCalendarStatus();
      setYandexCalendarStatus(res || null);
      setYandexSyncOptions({
        direction: String(res?.syncDirection || "BOTH"),
        conflictPolicy: String(res?.conflictPolicy || "SKIP_IF_BOOKED"),
        daysBack: Math.max(0, Math.min(60, Number(res?.daysBack) || 7)),
        daysForward: Math.max(1, Math.min(365, Number(res?.daysForward) || 180)),
        exportCalendarUrl: String(res?.exportCalendarUrl || res?.calendarUrl || ""),
        busyCalendarUrls: Array.isArray(res?.busyCalendarUrls) ? res.busyCalendarUrls.map((item) => String(item || "")).filter(Boolean) : [],
      });
      if (!res?.connected) {
        setYandexSyncConfiguratorOpen(false);
      } else {
        setYandexCalendarConsentChecked(false);
      }
    } catch (err) {
      setYandexCalendarStatus(null);
      toast.error(resolveCalendarErrorMessage(err, { provider: "Яндекс Календарь", fallback: "Не удалось загрузить Яндекс Календарь" }));
    } finally {
      setYandexCalendarLoading(false);
    }
  }, [canManageIntegrations, toast]);

  const resolvePeopleByIds = React.useCallback(async (ids = []) => {
    const uniqIds = Array.from(new Set(asArray(ids).map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0)));
    if (!uniqIds.length) return [];

    const resolved = await psychologistsApi.getMany(uniqIds, { pageSize: 200, maxPages: 8, concurrency: 8 });
    return asArray(resolved).map((item) => normalizePersonCard(item, "Психолог"));
  }, []);

  const loadAppointmentsAndPayments = React.useCallback(async () => {
    setAppointmentsLoading(true);
    try {
      const [appointmentsRes, paymentsRes] = await Promise.all([
        appointmentsApi.listMyAll().catch(() => []),
        paymentsApi.listMyPaged({ page: 0, size: 200 }).catch(() => ({ items: [] })),
      ]);
      const allAppointments = asArray(appointmentsRes).map(normalizeAppointment);
      allAppointments.sort((a, b) => {
        const da = parseDateValue(a.startAt)?.getTime() || 0;
        const db = parseDateValue(b.startAt)?.getTime() || 0;
        return da - db;
      });
      setAppointments(allAppointments);

      const allPayments = asArray(paymentsRes?.items).map(normalizePayment);
      allPayments.sort((a, b) => {
        const da = parseDateValue(a.createdAt)?.getTime() || 0;
        const db = parseDateValue(b.createdAt)?.getTime() || 0;
        return db - da;
      });
      setPayments(allPayments);
    } finally {
      setAppointmentsLoading(false);
    }
  }, []);

  const loadSubscriptions = React.useCallback(async () => {
    setSubscriptionsLoading(true);
    try {
      const [psySubRes, favRes, blogAuthorsRes, podcastRes, booksRes, podcastsCatalogRes, booksCatalogRes] = await Promise.all([
        psychologistsApi.subscriptions().catch(() => []),
        psychologistsApi.favourites().catch(() => []),
        blogApi.listAuthorSubscriptions().catch(() => []),
        podcastsApi.getMySubscriptions({ page: 0, size: 30 }).catch(() => ({ items: [] })),
        literatureApi.getMySaved({ page: 0, size: 30 }).catch(() => ({ items: [] })),
        podcastsApi.getCatalog({ page: 0, size: 3 }).catch(() => ({})),
        literatureApi.getCatalog({ page: 0, size: 3 }).catch(() => ({})),
      ]);

      const psychSubIds = extractIdArray(psySubRes);
      const favRaw = Array.isArray(favRes) ? favRes : extractItems(favRes);
      const favIds = extractIdArray(favRaw);
      const authorIds = extractIdArray(blogAuthorsRes);
      const allIdsToResolve = Array.from(new Set([...psychSubIds, ...favIds, ...authorIds]));
      const resolvedPeople = await resolvePeopleByIds(allIdsToResolve);
      const resolvedPeopleById = new Map(
        asArray(resolvedPeople)
          .map((item) => [parsePositiveId(item?.id), item])
          .filter(([id]) => Boolean(id))
      );
      const pickResolvedPeople = (ids = []) =>
        asArray(ids)
          .map((id) => resolvedPeopleById.get(parsePositiveId(id)))
          .filter(Boolean);

      const psychSubItems = pickResolvedPeople(psychSubIds);
      setPsychSubscriptions(psychSubItems);

      if (favRaw.length && typeof favRaw[0] === "object") {
        setFavorites(favRaw.map((x) => normalizePersonCard(x, "Психолог")));
      } else {
        setFavorites(pickResolvedPeople(favIds));
      }

      const authorProfiles = pickResolvedPeople(authorIds);
      setBlogAuthors(authorProfiles);

      const podcastItems = extractItems(podcastRes).map((x) => {
        const raw = asObject(x);
        const source = asObject(raw.podcast || raw.item || raw.show || raw);
        const author = asObject(source.author);
        return {
          id: source.id ?? raw.id ?? raw.podcastId ?? null,
          routeId: source.slug || raw.slug || source.id || raw.id || raw.podcastId || null,
          title: String(source.title || source.name || raw.title || raw.name || "Подкаст"),
          subtitle: String(
            author.name ||
              source.authorName ||
              source.psychologistName ||
              raw.authorName ||
              raw.psychologistName ||
              source.description ||
              raw.description ||
              ""
          ),
          count: Number(source.episodesCount || source.episodes || raw.episodesCount || raw.episodes || 0),
        };
      });
      setPodcastSubscriptions(podcastItems);

      const bookItems = extractItems(booksRes).map((x) => {
        const raw = asObject(x);
        const source = asObject(raw.book || raw.item || raw.literatureBook || raw);
        return {
          id: source.id ?? raw.id ?? raw.bookId ?? null,
          routeId: source.slug || raw.slug || source.id || raw.id || raw.bookId || null,
          title: String(source.title || source.name || raw.title || raw.name || "Книга"),
          author: String(source.author || source.authorName || source.writer || raw.author || raw.authorName || raw.writer || ""),
        };
      });
      setSavedBooks(bookItems);

      const featuredPodcastsSource = (() => {
        const parsed = extractItems(podcastsCatalogRes);
        if (parsed.length) return parsed;
        return asArray(asObject(podcastsCatalogRes).podcasts);
      })();

      const featuredBooksSource = (() => {
        const parsed = extractItems(booksCatalogRes);
        if (parsed.length) return parsed;
        return asArray(asObject(booksCatalogRes).books);
      })();

      setFeaturedPodcasts(featuredPodcastsSource.slice(0, 3).map(normalizeSuggestedPodcast));
      setFeaturedBooks(featuredBooksSource.slice(0, 3).map(normalizeSuggestedBook));
    } finally {
      setSubscriptionsLoading(false);
    }
  }, [resolvePeopleByIds]);

  const loadContent = React.useCallback(async () => {
    setContentLoading(true);
    try {
      const [forumTopicsRes, blogPostsRes, forumCommentsRes, blogCommentsRes, podcastCommentsRes, literatureCommentsRes] = await Promise.all([
        forumApi.listMyTopics({ page: 0, size: 30 }).catch(() => ({ content: [] })),
        request("/api/me/content/blog/posts?page=0&size=30").catch(() => ({ items: [] })),
        forumApi.listMyComments({ page: 0, size: 50 }).catch(() => ({ content: [] })),
        request("/api/me/content/blog/comments?page=0&size=50").catch(() => ({ items: [] })),
        request("/api/me/content/podcasts/comments?page=0&size=50").catch(() => ({ items: [] })),
        request("/api/me/content/literature/comments?page=0&size=50").catch(() => ({ items: [] })),
      ]);

      setForumTopics(asArray(forumTopicsRes?.content));

      const blogPostsPage = extractPage(blogPostsRes);
      setBlogPosts(asArray(blogPostsPage?.content));

      const commentsMerged = [
        ...asArray(forumCommentsRes?.content).map((x) => ({ ...x, category: "forum" })),
        ...asArray(extractPage(blogCommentsRes)?.content).map((x) => ({ ...x, category: "blog" })),
        ...asArray(extractPage(podcastCommentsRes)?.content).map((x) => ({ ...x, category: "podcast" })),
        ...asArray(extractPage(literatureCommentsRes)?.content).map((x) => ({ ...x, category: "literature" })),
      ];

      commentsMerged.sort((a, b) => {
        const da = parseDateValue(a.createdWhen || a.createdAt)?.getTime() || 0;
        const db = parseDateValue(b.createdWhen || b.createdAt)?.getTime() || 0;
        return db - da;
      });
      setComments(commentsMerged);
    } finally {
      setContentLoading(false);
    }
  }, []);

  const loadExtraStats = React.useCallback(async () => {
    try {
      const [testsRes, diaryRes] = await Promise.all([
        request("/api/me/tests/assigned?page=0&size=1").catch(() => null),
        request("/api/me/diary/entries").catch(() => null),
      ]);

      const testsPage = extractPage(testsRes || {});
      const testsTotal = Number(testsPage?.totalElements);
      if (Number.isFinite(testsTotal) && testsTotal >= 0) {
        setTestsAssignedCount(testsTotal);
      } else {
        setTestsAssignedCount(asArray(testsPage?.content).length);
      }

      const diaryItems = extractItems(diaryRes || {});
      setDiaryEntriesCount(diaryItems.length);
    } catch {
      setTestsAssignedCount(null);
      setDiaryEntriesCount(null);
    }
  }, []);

  const loadPsychologistSettings = React.useCallback(async () => {
    if (!isPsychologist) {
      setPsychologistProfileRaw({});
      setPsychologistThemeOptions([]);
      setPsychologistMethodOptions([]);
      setPsychologistSettingsForm(createEmptyPsychologistSettingsForm());
      setPsychologistSettingsBaseline(createEmptyPsychologistSettingsForm());
      return;
    }

    setPsychologistSettingsLoading(true);
    try {
      const [profileRes, themesRes, methodsRes] = await Promise.all([
        profileApi.getMyPsychologistProfile(),
        dictApi.themes().catch(() => []),
        dictApi.methods().catch(() => []),
      ]);

      const rawProfile = asObject(profileRes);
      const baseThemeOptions = normalizeDictOptions(themesRes, "theme");
      const baseMethodOptions = normalizeDictOptions(methodsRes, "method");
      const selectedThemeFallback = resolveDictSelection(
        rawProfile.themes,
        rawProfile.themeIds ?? rawProfile.theme_ids,
        baseThemeOptions,
        "theme"
      );
      const selectedMethodFallback = resolveDictSelection(
        rawProfile.methods,
        rawProfile.methodIds ?? rawProfile.method_ids,
        baseMethodOptions,
        "method"
      );
      const themeOptions = baseThemeOptions.length ? baseThemeOptions : selectedThemeFallback;
      const methodOptions = baseMethodOptions.length ? baseMethodOptions : selectedMethodFallback;

      const normalizedForm = normalizePsychologistSettingsForm(
        rawProfile,
        themeOptions,
        methodOptions
      );
      setPsychologistProfileRaw(rawProfile);
      setPsychologistThemeOptions(themeOptions);
      setPsychologistMethodOptions(methodOptions);
      setPsychologistSettingsForm(normalizedForm);
      setPsychologistSettingsBaseline(normalizedForm);
    } catch (err) {
      toast.error(err?.message || "Не удалось загрузить профиль психолога");
      setPsychologistProfileRaw({});
      setPsychologistSettingsForm(createEmptyPsychologistSettingsForm());
      setPsychologistSettingsBaseline(createEmptyPsychologistSettingsForm());
      setPsychologistThemeOptions([]);
      setPsychologistMethodOptions([]);
    } finally {
      setPsychologistSettingsLoading(false);
    }
  }, [isPsychologist, toast]);

  React.useEffect(() => {
    if (!me?.id) return;
    loadThemeSettings();
    loadNotificationSettings();
    loadPsychologistChatPolicy();
    loadPushDevices();
    if (QUASAR_INTEGRATION_ENABLED) {
      loadQuasarSettings();
    }
    loadGoogleCalendarStatus();
    loadYandexCalendarStatus();
    loadAppointmentsAndPayments();
    loadSubscriptions();
    loadContent();
    loadExtraStats();
    loadPsychologistSettings();
  }, [
    me?.id,
    loadThemeSettings,
    loadNotificationSettings,
    loadPsychologistChatPolicy,
    loadPushDevices,
    loadQuasarSettings,
    loadGoogleCalendarStatus,
    loadYandexCalendarStatus,
    loadAppointmentsAndPayments,
    loadSubscriptions,
    loadContent,
    loadExtraStats,
    loadPsychologistSettings,
  ]);

  React.useEffect(() => {
    const tabFromQuery = resolveProfileTab(searchParams.get("tab"), profileTabs);
    setActiveTab((prev) => (prev === tabFromQuery ? prev : tabFromQuery));
  }, [searchParams, profileTabs]);

  React.useEffect(() => {
    if (!canManageIntegrations) return;
    const code = String(searchParams.get("code") || "").trim();
    const state = String(searchParams.get("state") || "").trim();
    if (!code || !state) return;

    let provider = "google";
    try {
      provider = String(window.sessionStorage.getItem(CALENDAR_OAUTH_PROVIDER_KEY) || "google").trim().toLowerCase() || "google";
    } catch {}

    let cancelled = false;
    (async () => {
      try {
        if (provider === "yandex") {
          setYandexCalendarActionLoading(true);
        } else {
          setGoogleCalendarActionLoading(true);
        }
        const next = provider === "yandex"
          ? await profileApi.connectMyYandexCalendar({ code, state })
          : await profileApi.connectMyGoogleCalendar({ code, state });
        if (cancelled) return;
        if (provider === "yandex") {
          setYandexCalendarStatus(next || null);
          toast.success("Яндекс Календарь подключён");
        } else {
          setGoogleCalendarStatus(next || null);
          toast.success("Google Calendar подключён");
        }
        setActiveTab("integrations");
      } catch (err) {
        if (cancelled) return;
        toast.error(resolveCalendarErrorMessage(err, {
          provider: provider === "yandex" ? "Яндекс Календарь" : "Google Calendar",
          fallback:
            provider === "yandex"
              ? "Не удалось завершить подключение Яндекс Календаря"
              : "Не удалось завершить подключение Google Calendar",
        }));
      } finally {
        if (!cancelled) {
          if (provider === "yandex") {
            setYandexCalendarActionLoading(false);
          } else {
            setGoogleCalendarActionLoading(false);
          }
          try {
            window.sessionStorage.removeItem(CALENDAR_OAUTH_PROVIDER_KEY);
          } catch {}
          const params = new URLSearchParams(searchParams);
          params.set("tab", "integrations");
          params.delete("code");
          params.delete("state");
          setSearchParams(params, { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canManageIntegrations, searchParams, setSearchParams, toast]);

  const switchTab = React.useCallback(
    (nextTab) => {
      const resolvedTab = resolveProfileTab(nextTab, profileTabs);
      setActiveTab(resolvedTab);
      const params = new URLSearchParams(searchParams);
      if (resolvedTab === "profile") {
        params.delete("tab");
      } else {
        params.set("tab", resolvedTab);
      }
      setSearchParams(params, { replace: true });
    },
    [profileTabs, searchParams, setSearchParams]
  );

  const upcomingAppointments = React.useMemo(
    () => appointments.filter((x) => x.isUpcoming).sort((a, b) => (parseDateValue(a.startAt)?.getTime() || 0) - (parseDateValue(b.startAt)?.getTime() || 0)),
    [appointments]
  );

  const pastAppointments = React.useMemo(
    () => appointments.filter((x) => !x.isUpcoming).sort((a, b) => (parseDateValue(b.startAt)?.getTime() || 0) - (parseDateValue(a.startAt)?.getTime() || 0)),
    [appointments]
  );

  const commentsFiltered = React.useMemo(() => {
    if (commentFilter === "all") return comments;
    return comments.filter((x) => String(x.category) === commentFilter);
  }, [comments, commentFilter]);

  React.useEffect(() => {
    setListPages((prev) => ({ ...prev, comments: 1 }));
  }, [commentFilter]);

  const psychologistDirtyBySection = React.useMemo(() => {
    const current = buildPsychologistSectionSnapshot(psychologistSettingsForm);
    const baseline = buildPsychologistSectionSnapshot(psychologistSettingsBaseline);
    const profile = !areEqualSectionSnapshots(current.profile, baseline.profile);
    const work = !areEqualSectionSnapshots(current.work, baseline.work);
    const educationContacts = !areEqualSectionSnapshots(
      current.educationContacts,
      baseline.educationContacts
    );
    const certificates = !areEqualSectionSnapshots(
      current.certificates,
      baseline.certificates
    );
    return {
      profile,
      work,
      educationContacts,
      certificates,
      any: profile || work || educationContacts || certificates,
    };
  }, [psychologistSettingsForm, psychologistSettingsBaseline]);

  const setListPage = React.useCallback((key, nextPage) => {
    setListPages((prev) => ({
      ...prev,
      [key]: Math.max(1, Number(nextPage) || 1),
    }));
  }, []);

  const pagedUpcomingAppointments = React.useMemo(
    () => paginateItems(upcomingAppointments, listPages.upcomingAppointments, SETTINGS_PAGE_SIZES.upcomingAppointments),
    [upcomingAppointments, listPages.upcomingAppointments]
  );
  const pagedPastAppointments = React.useMemo(
    () => paginateItems(pastAppointments, listPages.pastAppointments, SETTINGS_PAGE_SIZES.pastAppointments),
    [pastAppointments, listPages.pastAppointments]
  );
  const pagedPayments = React.useMemo(
    () => paginateItems(payments, listPages.payments, SETTINGS_PAGE_SIZES.payments),
    [payments, listPages.payments]
  );
  const pagedPsychSubscriptions = React.useMemo(
    () => paginateItems(psychSubscriptions, listPages.psychSubscriptions, SETTINGS_PAGE_SIZES.psychSubscriptions),
    [psychSubscriptions, listPages.psychSubscriptions]
  );
  const pagedFavorites = React.useMemo(
    () => paginateItems(favorites, listPages.favorites, SETTINGS_PAGE_SIZES.favorites),
    [favorites, listPages.favorites]
  );
  const pagedBlogAuthors = React.useMemo(
    () => paginateItems(blogAuthors, listPages.blogAuthors, SETTINGS_PAGE_SIZES.blogAuthors),
    [blogAuthors, listPages.blogAuthors]
  );
  const pagedPodcastSubscriptions = React.useMemo(
    () => paginateItems(podcastSubscriptions, listPages.podcastSubscriptions, SETTINGS_PAGE_SIZES.podcastSubscriptions),
    [podcastSubscriptions, listPages.podcastSubscriptions]
  );
  const pagedSavedBooks = React.useMemo(
    () => paginateItems(savedBooks, listPages.savedBooks, SETTINGS_PAGE_SIZES.savedBooks),
    [savedBooks, listPages.savedBooks]
  );
  const pagedForumTopics = React.useMemo(
    () => paginateItems(forumTopics, listPages.forumTopics, SETTINGS_PAGE_SIZES.forumTopics),
    [forumTopics, listPages.forumTopics]
  );
  const pagedBlogPosts = React.useMemo(
    () => paginateItems(blogPosts, listPages.blogPosts, SETTINGS_PAGE_SIZES.blogPosts),
    [blogPosts, listPages.blogPosts]
  );
  const pagedComments = React.useMemo(
    () => paginateItems(commentsFiltered, listPages.comments, SETTINGS_PAGE_SIZES.comments),
    [commentsFiltered, listPages.comments]
  );

  const totalSpent = React.useMemo(
    () => payments.reduce((acc, p) => acc + (Number(p.amount) || 0), 0),
    [payments]
  );

  const stats = React.useMemo(
    () => ({
      sessionsDone: pastAppointments.length,
      testsDone: testsAssignedCount,
      diaryEntries: diaryEntriesCount,
      savedBooks: savedBooks.length,
      subscriptions: psychSubscriptions.length + podcastSubscriptions.length + blogAuthors.length,
      comments: comments.length,
    }),
    [
      pastAppointments.length,
      testsAssignedCount,
      diaryEntriesCount,
      savedBooks.length,
      psychSubscriptions.length,
      podcastSubscriptions.length,
      blogAuthors.length,
      comments.length,
    ]
  );

  const handleThemeChange = async (nextTheme) => {
    const normalized = nextTheme === "dark" ? "dark" : "light";
    if (normalized === theme || themeSaving) return;
    const prev = theme;
    setTheme(normalized);
    persistTheme(normalized);
    setThemeSaving(true);
    try {
      await profileApi.updateUiSettings({ theme: normalized });
      toast.success("Тема сохранена");
    } catch (err) {
      setTheme(prev);
      persistTheme(prev);
      toast.error(err?.message || "Не удалось сохранить тему");
    } finally {
      setThemeSaving(false);
    }
  };

  const handleRestartOnboarding = React.useCallback(async () => {
    if (restartingOnboarding) return;
    setRestartingOnboarding(true);
    try {
      await onboardingApi.restart();
      toast.success("Подсказки снова включены");
    } catch (error) {
      toast.error(error?.message || "Не удалось перезапустить подсказки");
    } finally {
      setRestartingOnboarding(false);
    }
  }, [restartingOnboarding, toast]);

  const handleToggleNotification = async (key, checked) => {
    setSavingNotificationKey(key);
    try {
      const updated = await notificationsApi.updateSettings({ [key]: checked });
      setNotificationSettings(asObject(updated));
    } catch (err) {
      toast.error(err?.message || "Не удалось обновить уведомления");
    } finally {
      setSavingNotificationKey("");
    }
  };

  const handlePsychologistChatPolicyChange = async (nextPolicyRaw) => {
    if (!isPsychologist || savingPsychologistChatPolicy) return;
    const nextPolicy = normalizeChatPolicyValue(nextPolicyRaw);
    if (nextPolicy === psychologistChatPolicy) return;

    const prevPolicy = psychologistChatPolicy;
    setPsychologistChatPolicy(nextPolicy);
    setSavingPsychologistChatPolicy(true);

    try {
      const updated = await profileApi.updateMyPsychologistChatSettings({
        inboundPolicy: nextPolicy,
      });
      const appliedPolicy = normalizeChatPolicyValue(
        updated?.inboundPolicy ?? updated?.directMessagesPolicy
      );
      setPsychologistChatPolicy(appliedPolicy);
      toast.success("Настройка сообщений обновлена");
    } catch (err) {
      setPsychologistChatPolicy(prevPolicy);
      const code = String(err?.code || "").trim().toUpperCase();
      if (code === "ONLY_PSYCHOLOGIST") {
        toast.error("Менять эту настройку может только психолог.");
      } else if (code === "CHAT_INBOUND_POLICY_REQUIRED") {
        toast.error("Выберите политику входящих сообщений.");
      } else if (code === "CHAT_INBOUND_POLICY_INVALID") {
        toast.error("Передано некорректное значение политики.");
      } else {
        toast.error(err?.message || "Не удалось сохранить настройку сообщений");
      }
    } finally {
      setSavingPsychologistChatPolicy(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (savingProfile) return;

    const normalizedName = String(fullName || "").trim().replace(/\s+/g, " ");
    const nameErr = validateFullName(normalizedName);
    if (nameErr) {
      toast.error(nameErr);
      return;
    }

    setSavingProfile(true);
    try {
      await profileApi.updateMe({ name: normalizedName, login: String(login || "").trim() });
      await boot();
      toast.success("Профиль сохранён");
    } catch (err) {
      toast.error(err?.message || "Не удалось сохранить профиль");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePassword = async () => {
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      toast.error("Заполните все поля пароля");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error("Пароли не совпадают");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Пароль должен быть не короче 8 символов");
      return;
    }

    setChangingPassword(true);
    try {
      await profileApi.changePassword({
        currentPassword,
        newPassword,
        confirmNewPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      toast.success("Пароль изменён");
    } catch (err) {
      toast.error(err?.message || "Не удалось изменить пароль");
    } finally {
      setChangingPassword(false);
    }
  };

  const triggerAvatarUpload = () => {
    if (!fileInputRef.current) return;
    fileInputRef.current.click();
  };

  const triggerCoverUpload = () => {
    if (!coverFileInputRef.current) return;
    coverFileInputRef.current.click();
  };

  const handleAvatarSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!imageTypeAllowed(file)) {
      toast.error("Аватар: только JPG, PNG или WEBP");
      return;
    }

    if (file.size > AVATAR_MAX_BYTES) {
      toast.error(`Аватар: максимальный размер ${formatBytesMb(AVATAR_MAX_BYTES)}`);
      return;
    }

    let dimensions = null;
    try {
      dimensions = await readImageDimensions(file);
    } catch {
      toast.error("Не удалось прочитать размеры изображения");
      return;
    }
    if (dimensions.width < AVATAR_MIN_SIDE_PX || dimensions.height < AVATAR_MIN_SIDE_PX) {
      toast.error(`Аватар: минимум ${AVATAR_MIN_SIDE_PX}×${AVATAR_MIN_SIDE_PX}px`);
      return;
    }

    setUploadingAvatar(true);
    try {
      const res = await profileApi.uploadAvatar(file);
      if (res?.avatarUrl) {
        setAvatarUrl(String(res.avatarUrl));
      }
      await boot();
      toast.success("Аватар обновлён");
    } catch (err) {
      toast.error(err?.message || "Не удалось загрузить аватар");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleCoverSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!imageTypeAllowed(file)) {
      toast.error("Фон: только JPG, PNG или WEBP");
      return;
    }
    if (file.size > COVER_MAX_BYTES) {
      toast.error(`Фон: максимальный размер ${formatBytesMb(COVER_MAX_BYTES)}`);
      return;
    }

    let dimensions = null;
    try {
      dimensions = await readImageDimensions(file);
    } catch {
      toast.error("Не удалось прочитать размеры изображения");
      return;
    }
    if (dimensions.width < COVER_MIN_WIDTH_PX || dimensions.height < COVER_MIN_HEIGHT_PX) {
      toast.error(`Фон: минимум ${COVER_MIN_WIDTH_PX}×${COVER_MIN_HEIGHT_PX}px`);
      return;
    }
    if (dimensions.width < dimensions.height) {
      toast.error("Фон должен быть горизонтальным изображением");
      return;
    }

    setUploadingCover(true);
    try {
      const uploadResponse = await filesApi.uploadImage(file);
      const uploadedUrl = extractUploadedFileUrl(uploadResponse);
      if (!uploadedUrl) {
        throw new Error("Сервер не вернул URL загруженного файла");
      }

      let updated;
      try {
        updated = await profileApi.updateMyPsychologistCover(uploadedUrl);
      } catch (coverUpdateError) {
        const status = Number(coverUpdateError?.status || 0);
        if (status !== 404 && status !== 405) {
          throw coverUpdateError;
        }

        let sourceProfile = asObject(psychologistProfileRaw);
        if (!Object.keys(sourceProfile).length) {
          sourceProfile = asObject(await profileApi.getMyPsychologistProfile());
        }
        updated = await profileApi.updateMyPsychologistProfile({
          ...sourceProfile,
          coverImageUrl: uploadedUrl,
        });
      }

      const normalizedProfile = asObject(updated);
      if (!cleanText(normalizedProfile.coverImageUrl)) {
        normalizedProfile.coverImageUrl = uploadedUrl;
      }
      setPsychologistProfileRaw(normalizedProfile);
      if (isPsychologist) {
        const normalizedForm = normalizePsychologistSettingsForm(
          normalizedProfile,
          psychologistThemeOptions,
          psychologistMethodOptions
        );
        setPsychologistSettingsForm(normalizedForm);
        setPsychologistSettingsBaseline(normalizedForm);
      }

      toast.success("Фон профиля обновлён");
    } catch (err) {
      toast.error(err?.message || "Не удалось загрузить фон профиля");
    } finally {
      setUploadingCover(false);
    }
  };

  const handleAvatarDelete = async () => {
    if (!me?.id) return;
    setUploadingAvatar(true);
    try {
      await profileApi.deleteAvatar(me.id);
      setAvatarUrl("");
      await boot();
      toast.success("Аватар удалён");
    } catch (err) {
      toast.error(err?.message || "Не удалось удалить аватар");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch {
      navigate("/login");
    }
  };

  const handleDisconnectDevice = async (device) => {
    const key = `${device?.channel || ""}:${device?.id || ""}`;
    if (!device?.id || disconnectingDevice) return;
    setDisconnectingDevice(key);
    try {
      await pwaApi.disconnectPushDevice(device.channel, device.id);
      await loadPushDevices();
      toast.success("Устройство отключено");
    } catch (err) {
      toast.error(err?.message || "Не удалось отключить устройство");
    } finally {
      setDisconnectingDevice("");
    }
  };

  const handleConnectQuasar = React.useCallback(async () => {
    if (!canManageQuasarIntegration) return;
    try {
      setQuasarActionLoading(true);
      const next = await profileApi.connectMyQuasar({});
      setQuasar(next || null);
      toast.success("Quasar подключение запущено");
    } catch (e) {
      const msg = String(e?.message || "");
      if (msg.includes("QUASAR_WEBHOOK_NOT_CONFIGURED")) {
        toast.error("Интеграция Quasar не готова: webhook не настроен");
      } else {
        toast.error(msg || "Не удалось подключить Quasar");
      }
    } finally {
      setQuasarActionLoading(false);
    }
  }, [canManageQuasarIntegration, toast]);

  const handleDisconnectQuasar = React.useCallback(async () => {
    if (!canManageQuasarIntegration) return;
    try {
      setQuasarActionLoading(true);
      const next = await profileApi.disconnectMyQuasar();
      setQuasar(next || null);
      toast.success("Quasar отключён");
    } catch (e) {
      toast.error(e?.message || "Не удалось отключить Quasar");
    } finally {
      setQuasarActionLoading(false);
    }
  }, [canManageQuasarIntegration, toast]);

  const handleToggleQuasarField = React.useCallback(
    async (field, value) => {
      if (!quasar || !canManageQuasarIntegration) return;
      try {
        setQuasarActionLoading(true);
        const next = await profileApi.updateMyQuasarSettings({
          ...quasar,
          notifications: {
            ...(quasar.notifications || {}),
            [field]: Boolean(value),
          },
        });
        setQuasar(next || null);
      } catch (e) {
        toast.error(e?.message || "Не удалось сохранить настройки Quasar");
      } finally {
        setQuasarActionLoading(false);
      }
    },
    [quasar, canManageQuasarIntegration, toast]
  );

  const handleConnectGoogleCalendar = React.useCallback(async () => {
    if (!canManageIntegrations) return;
    try {
      setGoogleCalendarActionLoading(true);
      const res = await profileApi.getMyGoogleCalendarAuthUrl({
        consentAccepted: true,
        documentVersion: GOOGLE_CALENDAR_CONSENT_VERSION,
        sourcePage: resolveConsentSourcePage(),
      });
      const authUrl = String(res?.authUrl || "").trim();
      if (!res?.enabled) {
        toast.error("Интеграция Google Calendar выключена");
        return;
      }
      if (!res?.configured) {
        toast.error("Google Calendar не настроен на сервере");
        return;
      }
      if (!authUrl) {
        toast.error("Сервер не вернул ссылку подключения");
        return;
      }
      try {
        window.sessionStorage.setItem(CALENDAR_OAUTH_PROVIDER_KEY, "google");
      } catch {}
      window.location.assign(authUrl);
    } catch (e) {
      toast.error(resolveCalendarErrorMessage(e, { provider: "Google Calendar", fallback: "Не удалось начать подключение Google Calendar" }));
    } finally {
      setGoogleCalendarActionLoading(false);
    }
  }, [canManageIntegrations, toast]);

  const handleDisconnectGoogleCalendar = React.useCallback(async () => {
    if (!canManageIntegrations) return;
    try {
      setGoogleCalendarActionLoading(true);
      const next = await profileApi.disconnectMyGoogleCalendar();
      setGoogleCalendarStatus(next || null);
      setGoogleCalendarConsentChecked(false);
      setGoogleSyncConfiguratorOpen(false);
      toast.success("Google Calendar отключён");
    } catch (e) {
      toast.error(resolveCalendarErrorMessage(e, { provider: "Google Calendar", fallback: "Не удалось отключить Google Calendar" }));
    } finally {
      setGoogleCalendarActionLoading(false);
    }
  }, [canManageIntegrations, toast]);

  const handleGoogleSyncOptionChange = React.useCallback((key, value) => {
    setGoogleSyncOptions((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleToggleGoogleBusyCalendar = React.useCallback((calendarId) => {
    const id = String(calendarId || "").trim();
    if (!id) return;
    setGoogleSyncOptions((prev) => {
      const current = Array.isArray(prev?.busyCalendarIds) ? prev.busyCalendarIds : [];
      const next = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
      return { ...prev, busyCalendarIds: next };
    });
  }, []);

  const handleSaveGoogleCalendarPreferences = React.useCallback(async () => {
    if (!canManageIntegrations) return;
    try {
      setGoogleCalendarActionLoading(true);
      const next = await profileApi.saveMyGoogleCalendarPreferences({
        exportCalendarId: String(googleSyncOptions?.exportCalendarId || googleCalendarStatus?.exportCalendarId || "primary"),
        busyCalendarIds: Array.isArray(googleSyncOptions?.busyCalendarIds) ? googleSyncOptions.busyCalendarIds : [],
        direction: String(googleSyncOptions?.direction || "BIDIRECTIONAL"),
        conflictPolicy: String(googleSyncOptions?.conflictPolicy || "SKIP_IF_BOOKED"),
        daysBack: Math.max(0, Math.min(60, Number(googleSyncOptions?.daysBack) || 7)),
        daysForward: Math.max(1, Math.min(365, Number(googleSyncOptions?.daysForward) || 180)),
      });
      setGoogleCalendarStatus(next || null);
      toast.success("Настройки Google Calendar сохранены");
    } catch (e) {
      toast.error(resolveCalendarErrorMessage(e, { provider: "Google Calendar", fallback: "Не удалось сохранить настройки Google Calendar" }));
    } finally {
      setGoogleCalendarActionLoading(false);
    }
  }, [canManageIntegrations, googleSyncOptions, googleCalendarStatus, toast]);

  const handleSyncGoogleCalendar = React.useCallback(async () => {
    if (!canManageIntegrations) return;
    try {
      setGoogleCalendarSyncing(true);
      const payload = {
        direction: String(googleSyncOptions?.direction || "BIDIRECTIONAL"),
        fromGoogleMode: "MARK_AS_WORK_BREAKS",
        conflictPolicy: String(googleSyncOptions?.conflictPolicy || "SKIP_IF_BOOKED"),
        daysBack: Math.max(0, Math.min(60, Number(googleSyncOptions?.daysBack) || 7)),
        daysForward: Math.max(1, Math.min(365, Number(googleSyncOptions?.daysForward) || 180)),
        exportCalendarId: String(googleSyncOptions?.exportCalendarId || googleCalendarStatus?.exportCalendarId || "primary"),
        busyCalendarIds: Array.isArray(googleSyncOptions?.busyCalendarIds) ? googleSyncOptions.busyCalendarIds : [],
      };
      const result = await profileApi.syncMyGoogleCalendar(payload);
      const synced = Number(result?.syncedCount || 0);
      const failed = Number(result?.failedCount || 0);
      const msg = String(result?.message || "").trim();
      toast.success(msg || (failed > 0 ? `Синхронизировано: ${synced}, ошибок: ${failed}` : `Синхронизировано: ${synced}`));
      await loadGoogleCalendarStatus();
    } catch (e) {
      toast.error(resolveCalendarErrorMessage(e, { provider: "Google Calendar", fallback: "Не удалось синхронизировать Google Calendar" }));
    } finally {
      setGoogleCalendarSyncing(false);
    }
  }, [canManageIntegrations, toast, loadGoogleCalendarStatus, googleSyncOptions, googleCalendarStatus]);

  const handleConnectYandexCalendar = React.useCallback(async () => {
    if (!canManageIntegrations) return;
    try {
      setYandexCalendarActionLoading(true);
      const res = await profileApi.getMyYandexCalendarAuthUrl({
        consentAccepted: true,
        documentVersion: YANDEX_CALENDAR_CONSENT_VERSION,
        sourcePage: resolveConsentSourcePage(),
      });
      const authUrl = String(res?.authUrl || "").trim();
      if (!res?.enabled) {
        toast.error("Интеграция Яндекс Календаря выключена");
        return;
      }
      if (!res?.configured) {
        toast.error("Яндекс Календарь не настроен на сервере");
        return;
      }
      if (!authUrl) {
        toast.error("Сервер не вернул ссылку подключения");
        return;
      }
      try {
        window.sessionStorage.setItem(CALENDAR_OAUTH_PROVIDER_KEY, "yandex");
      } catch {}
      window.location.assign(authUrl);
    } catch (e) {
      toast.error(resolveCalendarErrorMessage(e, { provider: "Яндекс Календарь", fallback: "Не удалось начать подключение Яндекс Календаря" }));
    } finally {
      setYandexCalendarActionLoading(false);
    }
  }, [canManageIntegrations, toast]);

  const handleDisconnectYandexCalendar = React.useCallback(async () => {
    if (!canManageIntegrations) return;
    try {
      setYandexCalendarActionLoading(true);
      const next = await profileApi.disconnectMyYandexCalendar();
      setYandexCalendarStatus(next || null);
      setYandexCalendarConsentChecked(false);
      setYandexSyncConfiguratorOpen(false);
      toast.success("Яндекс Календарь отключён");
    } catch (e) {
      toast.error(resolveCalendarErrorMessage(e, { provider: "Яндекс Календарь", fallback: "Не удалось отключить Яндекс Календарь" }));
    } finally {
      setYandexCalendarActionLoading(false);
    }
  }, [canManageIntegrations, toast]);

  const handleYandexSyncOptionChange = React.useCallback((key, value) => {
    setYandexSyncOptions((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleToggleYandexBusyCalendar = React.useCallback((calendarUrl) => {
    const id = String(calendarUrl || "").trim();
    if (!id) return;
    setYandexSyncOptions((prev) => {
      const current = Array.isArray(prev?.busyCalendarUrls) ? prev.busyCalendarUrls : [];
      const next = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
      return { ...prev, busyCalendarUrls: next };
    });
  }, []);

  const handleSaveYandexCalendarPreferences = React.useCallback(async () => {
    if (!canManageIntegrations) return;
    try {
      setYandexCalendarActionLoading(true);
      const next = await profileApi.saveMyYandexCalendarPreferences({
        exportCalendarUrl: String(yandexSyncOptions?.exportCalendarUrl || yandexCalendarStatus?.exportCalendarUrl || ""),
        busyCalendarUrls: Array.isArray(yandexSyncOptions?.busyCalendarUrls) ? yandexSyncOptions.busyCalendarUrls : [],
        direction: String(yandexSyncOptions?.direction || "BOTH"),
        conflictPolicy: String(yandexSyncOptions?.conflictPolicy || "SKIP_IF_BOOKED"),
        daysBack: Math.max(0, Math.min(60, Number(yandexSyncOptions?.daysBack) || 7)),
        daysForward: Math.max(1, Math.min(365, Number(yandexSyncOptions?.daysForward) || 180)),
      });
      setYandexCalendarStatus(next || null);
      toast.success("Настройки Яндекс Календаря сохранены");
    } catch (e) {
      toast.error(resolveCalendarErrorMessage(e, { provider: "Яндекс Календарь", fallback: "Не удалось сохранить настройки Яндекс Календаря" }));
    } finally {
      setYandexCalendarActionLoading(false);
    }
  }, [canManageIntegrations, yandexSyncOptions, yandexCalendarStatus, toast]);

  const handleSyncYandexCalendar = React.useCallback(async () => {
    if (!canManageIntegrations) return;
    try {
      setYandexCalendarSyncing(true);
      const payload = {
        direction: String(yandexSyncOptions?.direction || "BOTH"),
        fromYandexMode: "MARK_AS_WORK_BREAKS",
        conflictPolicy: String(yandexSyncOptions?.conflictPolicy || "SKIP_IF_BOOKED"),
        daysBack: Math.max(0, Math.min(60, Number(yandexSyncOptions?.daysBack) || 7)),
        daysForward: Math.max(1, Math.min(365, Number(yandexSyncOptions?.daysForward) || 180)),
        exportCalendarUrl: String(yandexSyncOptions?.exportCalendarUrl || yandexCalendarStatus?.exportCalendarUrl || ""),
        busyCalendarUrls: Array.isArray(yandexSyncOptions?.busyCalendarUrls) ? yandexSyncOptions.busyCalendarUrls : [],
      };
      const result = await profileApi.syncMyYandexCalendar(payload);
      const synced = Number(result?.syncedCount || 0);
      const failed = Number(result?.failedCount || 0);
      const msg = String(result?.message || "").trim();
      toast.success(msg || (failed > 0 ? `Синхронизировано: ${synced}, ошибок: ${failed}` : `Синхронизировано: ${synced}`));
      await loadYandexCalendarStatus();
    } catch (e) {
      toast.error(resolveCalendarErrorMessage(e, { provider: "Яндекс Календарь", fallback: "Не удалось синхронизировать Яндекс Календарь" }));
    } finally {
      setYandexCalendarSyncing(false);
    }
  }, [canManageIntegrations, toast, loadYandexCalendarStatus, yandexSyncOptions, yandexCalendarStatus]);

  const handleSubscribePodcast = React.useCallback(
    async (podcast) => {
      const podcastId = String(podcast?.id || "").trim();
      if (!podcastId || subscribingPodcastId === podcastId) return;

      setSubscribingPodcastId(podcastId);
      try {
        await podcastsApi.subscribe(podcast.id);
        setPodcastSubscriptions((prev) => [
          {
            id: podcast.id,
            title: podcast.title,
            subtitle: podcast.authorName,
            count: podcast.episodesCount,
          },
          ...prev.filter((item) => String(item.id) !== podcastId),
        ]);
        toast.success(podcast?.title ? `Подписка на «${podcast.title}» оформлена` : "Подписка оформлена");
      } catch (err) {
        toast.error(err?.message || "Не удалось подписаться на подкаст");
      } finally {
        setSubscribingPodcastId("");
      }
    },
    [subscribingPodcastId, toast]
  );

  const handleSaveSuggestedBook = React.useCallback(
    async (book) => {
      const bookId = String(book?.id || "").trim();
      if (!bookId || savingBookId === bookId) return;

      setSavingBookId(bookId);
      try {
        await literatureApi.saveBook(book.id);
        setSavedBooks((prev) => [
          {
            id: book.id,
            title: book.title,
            author: book.author,
          },
          ...prev.filter((item) => String(item.id) !== bookId),
        ]);
        toast.success(book?.title ? `Книга «${book.title}» сохранена` : "Книга сохранена");
      } catch (err) {
        toast.error(err?.message || "Не удалось сохранить книгу");
      } finally {
        setSavingBookId("");
      }
    },
    [savingBookId, toast]
  );

  const handleUnsubscribePsychologist = React.useCallback(
    async (person) => {
      const personId = String(person?.id || "").trim();
      if (!personId || unsubscribingPsychId === personId) return;

      setUnsubscribingPsychId(personId);
      try {
        await psychologistsApi.unsubscribe(person.id);
        setPsychSubscriptions((prev) => prev.filter((item) => String(item.id) !== personId));
        toast.success(person?.name ? `Подписка на ${person.name} отменена` : "Подписка отменена");
      } catch (err) {
        toast.error(err?.message || "Не удалось отменить подписку");
      } finally {
        setUnsubscribingPsychId("");
      }
    },
    [toast, unsubscribingPsychId]
  );

  const handleRemoveFavoritePsychologist = React.useCallback(
    async (person) => {
      const personId = String(person?.id || "").trim();
      if (!personId || removingFavoriteId === personId) return;

      setRemovingFavoriteId(personId);
      try {
        await psychologistsApi.removeFavourite(person.id);
        setFavorites((prev) => prev.filter((item) => String(item.id) !== personId));
        toast.success(person?.name ? `${person.name} убран(а) из избранного` : "Убрано из избранного");
      } catch (err) {
        toast.error(err?.message || "Не удалось убрать из избранного");
      } finally {
        setRemovingFavoriteId("");
      }
    },
    [removingFavoriteId, toast]
  );

  const handleUnsubscribeBlogAuthor = React.useCallback(
    async (author) => {
      const authorId = String(author?.id || "").trim();
      if (!authorId || unsubscribingBlogAuthorId === authorId) return;

      setUnsubscribingBlogAuthorId(authorId);
      try {
        await blogApi.toggleAuthorSubscription(author.id);
        setBlogAuthors((prev) => prev.filter((item) => String(item.id) !== authorId));
        toast.success(author?.name ? `Подписка на ${author.name} отменена` : "Подписка отменена");
      } catch (err) {
        toast.error(err?.message || "Не удалось отменить подписку на автора");
      } finally {
        setUnsubscribingBlogAuthorId("");
      }
    },
    [toast, unsubscribingBlogAuthorId]
  );

  const handleUnsubscribePodcast = React.useCallback(
    async (podcast) => {
      const podcastId = String(podcast?.id || "").trim();
      if (!podcastId || unsubscribingPodcastId === podcastId) return;

      setUnsubscribingPodcastId(podcastId);
      try {
        await podcastsApi.unsubscribe(podcast.id);
        setPodcastSubscriptions((prev) => prev.filter((item) => String(item.id) !== podcastId));
        toast.success(podcast?.title ? `Подписка на «${podcast.title}» отменена` : "Подписка отменена");
      } catch (err) {
        toast.error(err?.message || "Не удалось отменить подписку");
      } finally {
        setUnsubscribingPodcastId("");
      }
    },
    [toast, unsubscribingPodcastId]
  );

  const handleUnsaveBook = React.useCallback(
    async (book) => {
      const bookId = String(book?.id || "").trim();
      if (!bookId || unsavingBookId === bookId) return;

      setUnsavingBookId(bookId);
      try {
        await literatureApi.unsaveBook(book.id);
        setSavedBooks((prev) => prev.filter((item) => String(item.id) !== bookId));
        toast.success(book?.title ? `Книга «${book.title}» убрана из сохранённых` : "Книга убрана из сохранённых");
      } catch (err) {
        toast.error(err?.message || "Не удалось убрать книгу из сохранённых");
      } finally {
        setUnsavingBookId("");
      }
    },
    [toast, unsavingBookId]
  );

  const togglePsychologistDictionaryValue = (field, option) => {
    if (!option) return;
    setPsychologistSettingsForm((prev) => {
      const source = asArray(prev?.[field]);
      const key = String(option.id);
      const exists = source.some((item) => String(item?.id) === key);
      return {
        ...prev,
        [field]: exists ? source.filter((item) => String(item?.id) !== key) : [...source, option],
      };
    });
  };

  const updatePsychologistSettingsField = (field, value) => {
    setPsychologistSettingsForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePsychologistEducationChange = (index, field, value) => {
    setPsychologistSettingsForm((prev) => ({
      ...prev,
      education: asArray(prev.education).map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      ),
    }));
  };

  const handlePsychologistEducationAdd = () => {
    setPsychologistSettingsForm((prev) => ({
      ...prev,
      education: [...asArray(prev.education), createEducationRow({}, asArray(prev.education).length)],
    }));
  };

  const handlePsychologistEducationRemove = (index) => {
    setPsychologistSettingsForm((prev) => ({
      ...prev,
      education: asArray(prev.education).filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleSocialLinkChange = (index, field, value) => {
    setPsychologistSettingsForm((prev) => ({
      ...prev,
      socialLinks: asArray(prev.socialLinks).map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        if (field === "type") {
          const option = socialOptionByType(value);
          return { ...row, type: value, label: option.label };
        }
        return { ...row, [field]: value };
      }),
    }));
  };

  const handleSocialLinkAdd = () => {
    setPsychologistSettingsForm((prev) => ({
      ...prev,
      socialLinks: [...asArray(prev.socialLinks), createSocialRow({}, asArray(prev.socialLinks).length)],
    }));
  };

  const handleSocialLinkRemove = (index) => {
    setPsychologistSettingsForm((prev) => ({
      ...prev,
      socialLinks: asArray(prev.socialLinks).filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handlePsychologistCertificateChange = (index, field, value) => {
    setPsychologistSettingsForm((prev) => ({
      ...prev,
      certificates: asArray(prev.certificates).map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      ),
    }));
  };

  const handleEducationFileUpload = async (index, file) => {
    if (!file) return;
    setUploadingEducationFile({ index, fileName: file.name });
    try {
      const response = await filesApi.uploadEducationDocument(file);
      const uploadedUrl = response?.file?.url || response?.url || response?.documentUrl;
      if (uploadedUrl) {
        setPsychologistSettingsForm((prev) => ({
          ...prev,
          education: asArray(prev.education).map((row, rowIndex) =>
            rowIndex === index
              ? { ...row, fileUrl: uploadedUrl, fileName: file.name }
              : row
          ),
        }));
        toast.success("Документ загружен");
      } else {
        toast.error("Не удалось получить ссылку на загруженный файл");
      }
    } catch (err) {
      toast.error("Ошибка загрузки документа: " + (err?.message || "Неизвестная ошибка"));
    } finally {
      setUploadingEducationFile(null);
    }
  };

  const handleCertificateFileUpload = async (index, file) => {
    if (!file) return;
    setUploadingCertificateFile({ index, fileName: file.name });
    try {
      const response = await filesApi.uploadEducationDocument(file);
      const uploadedUrl = response?.file?.url || response?.url || response?.documentUrl;
      if (uploadedUrl) {
        setPsychologistSettingsForm((prev) => ({
          ...prev,
          certificates: asArray(prev.certificates).map((row, rowIndex) =>
            rowIndex === index
              ? { ...row, documentUrl: uploadedUrl, fileName: file.name }
              : row
          ),
        }));
        toast.success("Сертификат загружен");
      } else {
        toast.error("Не удалось получить ссылку на загруженный файл");
      }
    } catch (err) {
      toast.error("Ошибка загрузки сертификата: " + (err?.message || "Неизвестная ошибка"));
    } finally {
      setUploadingCertificateFile(null);
    }
  };

  const handlePsychologistCertificateAdd = () => {
    setPsychologistSettingsForm((prev) => ({
      ...prev,
      certificates: [...asArray(prev.certificates), createCertificateRow({}, asArray(prev.certificates).length)],
    }));
  };

  const handlePsychologistCertificateRemove = (index) => {
    setPsychologistSettingsForm((prev) => ({
      ...prev,
      certificates: asArray(prev.certificates).filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleSavePsychologistSettings = async () => {
    if (!isPsychologist || psychologistSettingsSaving) return;
    setPsychologistSettingsSaving(true);
    try {
      const payload = buildPsychologistProfilePayload(
        psychologistProfileRaw,
        psychologistSettingsForm
      );
      const updated = await profileApi.updateMyPsychologistProfile(payload);
      const updatedProfile = asObject(updated || payload);
      const normalizedForm = normalizePsychologistSettingsForm(
        updatedProfile,
        psychologistThemeOptions,
        psychologistMethodOptions
      );
      setPsychologistProfileRaw(updatedProfile);
      setPsychologistSettingsForm(normalizedForm);
      setPsychologistSettingsBaseline(normalizedForm);
      toast.success("Профиль психолога сохранён");
    } catch (err) {
      toast.error(err?.message || "Не удалось сохранить профиль психолога");
    } finally {
      setPsychologistSettingsSaving(false);
    }
  };

  const canManageLiterature = normalizedRole === "ADMIN" || normalizedRole === "MODERATOR" || normalizedRole === "PSYCHOLOGIST";
  const nextAppointment = upcomingAppointments[0] || null;
  const profileCoverImageUrl = React.useMemo(
    () =>
      cleanText(
        psychologistProfileRaw?.coverImageUrl ||
          psychologistProfileRaw?.cover_url ||
          psychologistProfileRaw?.coverUrl
      ),
    [psychologistProfileRaw]
  );

  const renderAppointmentItem = (item, upcoming = true) => (
    <div className="appt-item" key={`${upcoming ? "up" : "past"}-${item.id}-${item.startAt || "na"}`}>
      <div className="appt-date-block" style={!upcoming ? { background: "var(--bg)", borderColor: "var(--border)" } : undefined}>
        <div className="appt-date-d" style={!upcoming ? { color: "var(--mid)" } : undefined}>{dayToken(item.startAt)}</div>
        <div className="appt-date-m" style={!upcoming ? { color: "var(--mid)" } : undefined}>{monthToken(item.startAt)}</div>
      </div>
      <div className="appt-info">
        <div className="appt-name">{item.title}</div>
        <div className="appt-time">
          {formatTime(item.startAt)}{item.endAt ? ` – ${formatTime(item.endAt)}` : ""}
          {item.amount != null ? ` · ${formatPrice(item.amount)}` : ""}
        </div>
      </div>
      <span className={`appt-status ${upcoming ? "upcoming" : "past"}`}>
        {upcoming ? (item.status === "PAID" ? "Оплачено" : "Скоро") : "Прошла"}
      </span>
    </div>
  );

  const renderTabProfile = () => (
    <div className="tab-panel active" id="tab-profile">
      <div className="left-col">
        <div className="profile-hero">
          <div className="profile-cover">
            {profileCoverImageUrl ? <img src={profileCoverImageUrl} alt="" className="profile-cover-img" /> : null}
            <div className="profile-cover-pattern" />
          </div>
          <div className="profile-hero-body">
            <div className="avatar-area">
              <div className="profile-avatar-wrap">
                <button className="profile-avatar" type="button" onClick={triggerAvatarUpload}>
                  {avatarUrl ? <img src={avatarUrl} alt="Аватар" className="profile-avatar-img" /> : profileInitials(fullName, login)}
                  <span className="avatar-edit-overlay">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </span>
                </button>
                <span className="profile-online-dot" />
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden-input" onChange={handleAvatarSelect} />
                <input ref={coverFileInputRef} type="file" accept="image/*" className="hidden-input" onChange={handleCoverSelect} />
              </div>

              <div className="avatar-actions">
                <div className="avatar-actions-row">
                  <button className="btn-sm" type="button" onClick={triggerCoverUpload} disabled={uploadingCover}>
                    Загрузить баннер
                  </button>
                  <button className="btn-sm danger" type="button" onClick={handleAvatarDelete} disabled={uploadingAvatar || !avatarUrl}>
                    Удалить
                  </button>
                </div>
                <div className="avatar-rules">
                  <div>Аватар: JPG/PNG/WEBP, до 5MB, минимум 320×320px.</div>
                  <div>Фон профиля: JPG/PNG/WEBP, до 8MB, минимум 1200×300px (горизонтально).</div>
                </div>
              </div>
            </div>

            <div className="profile-name" id="display-name">{fullName || "Без имени"}</div>
            <div className="profile-login" id="display-login">@{login || "login"}</div>
            <div className="profile-meta-row">
              <span className="profile-badge role">{roleLabel(me?.role)}</span>
              <span className="profile-badge verified">Почта подтверждена</span>
            </div>
          </div>
        </div>

        <form className="panel-card" data-onboarding-target="profile-basic-fields" onSubmit={handleSaveProfile}>
          <div className="panel-card-header">
            <span className="panel-card-title">Личные данные</span>
            <div className="btn-group-inline">
              <button className="btn-sm" type="button" onClick={() => {
                setFullName(sanitizeFullNameInput(me?.name || ""));
                setLogin(String(me?.login || ""));
              }}>
                Отмена
              </button>
              <button className="btn-sm primary" type="submit" disabled={savingProfile}>Сохранить</button>
            </div>
          </div>
          <div className="panel-card-body">
            <div className="form-section">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Имя</label>
                  <input
                    className="form-input"
                    data-onboarding-target="profile-name-field"
                    type="text"
                    value={fullName}
                    placeholder="Ваше имя"
                    onChange={(e) => setFullName(sanitizeFullNameInput(e.target.value))}
                    onBlur={() => setFullName((v) => v.trim().replace(/\s+/g, " "))}
                  />
                  <span className="form-hint">Отображается в профиле и в системе</span>
                </div>
                <div className="form-group">
                  <label className="form-label">Логин</label>
                  <input
                    className="form-input"
                    data-onboarding-target="profile-login-field"
                    type="text"
                    value={login}
                    placeholder="login"
                    onChange={(e) => setLogin(e.target.value)}
                  />
                  <span className="form-hint">Уникальный идентификатор</span>
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Смена пароля</span>
          </div>
          <div className="panel-card-body">
            <div className="form-section">
              <div className="form-group">
                <label className="form-label">Текущий пароль</label>
                <input className="form-input" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Введите текущий пароль" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Новый пароль</label>
                  <input className="form-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Минимум 8 символов" />
                </div>
                <div className="form-group">
                  <label className="form-label">Подтверждение</label>
                  <input className="form-input" type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} placeholder="Повторите пароль" />
                </div>
              </div>
              <div className="action-right">
                <button className="btn-sm primary" type="button" onClick={handleSavePassword} disabled={changingPassword}>
                  Изменить пароль
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="right-col">
        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Активность</span>
          </div>
          <div className="panel-card-body panel-card-body--compact">
              <div className="stats-grid">
                <div className="stat-card accent-green"><div className="stat-num">{stats.sessionsDone}</div><div className="stat-label">Сессий проведено</div></div>
                <div className="stat-card accent-lav"><div className="stat-num">{stats.testsDone ?? "—"}</div><div className="stat-label">Тестов пройдено</div></div>
                <div className="stat-card"><div className="stat-num">{stats.diaryEntries ?? "—"}</div><div className="stat-label">Записей в дневнике</div></div>
                <div className="stat-card accent-green"><div className="stat-num">{stats.savedBooks}</div><div className="stat-label">Сохранённых книг</div></div>
                <div className="stat-card accent-lav"><div className="stat-num">{stats.subscriptions}</div><div className="stat-label">Подписок</div></div>
                <div className="stat-card"><div className="stat-num">{stats.comments}</div><div className="stat-label">Комментариев</div></div>
              </div>
          </div>
        </div>

        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Аккаунт</span>
          </div>
          <div className="panel-card-body panel-card-body--rows">
            <div className="field-row"><span className="field-key">Роль</span><span className="field-val">{roleLabel(me?.role)}</span></div>
            <div className="field-row"><span className="field-key">Последний вход</span><span className="field-val">{formatDateTime(resolveLastLoginValue(me))}</span></div>
            <div className="field-row">
              <span className="field-key">Тема</span>
              <span className="field-val" id="theme-label-display">{theme === "dark" ? "Тёмная" : "Светлая"}</span>
              <div className="field-action">
                <button className="btn-sm" type="button" onClick={() => switchTab("settings")}>Сменить</button>
              </div>
            </div>
          </div>
        </div>

        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Ближайшая сессия</span>
            <button className="btn-sm" type="button" onClick={() => switchTab("appointments")}>Все →</button>
          </div>
          <div className="panel-card-body">
            {nextAppointment ? (
              <div className="next-session-card">
                <div className="next-session-head">
                  <div className="next-session-date">
                    <div className="next-session-day">{dayToken(nextAppointment.startAt)}</div>
                    <div className="next-session-month">{monthToken(nextAppointment.startAt)}</div>
                  </div>
                  <div>
                    <div className="next-session-title">{nextAppointment.title}</div>
                    <div className="next-session-sub">{nextAppointment.type === "GROUP" ? "Групповая сессия" : "Индивидуальная сессия"}</div>
                  </div>
                </div>
                <div className="next-session-bottom">
                  <div className="next-session-time">{formatTime(nextAppointment.startAt)}</div>
                </div>
              </div>
            ) : (
              <div className="empty-row">Нет ближайших записей</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabAppointments = () => (
    <div className="tab-panel active" id="tab-appointments">
      <div className="left-col">
        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Предстоящие записи</span>
            <span className="badge-inline">{upcomingAppointments.length} активных</span>
          </div>
          <div className="panel-card-body panel-card-body--rows">
            {appointmentsLoading ? <div className="empty-row">Загрузка...</div> : null}
            {!appointmentsLoading && pagedUpcomingAppointments.totalItems === 0 ? <div className="empty-row">Нет предстоящих записей</div> : null}
            {!appointmentsLoading && pagedUpcomingAppointments.items.map((item) => renderAppointmentItem(item, true))}
          </div>
          {!appointmentsLoading ? renderPagination("upcomingAppointments", pagedUpcomingAppointments) : null}
        </div>

        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">История записей</span>
          </div>
          <div className="panel-card-body panel-card-body--rows">
            {appointmentsLoading ? <div className="empty-row">Загрузка...</div> : null}
            {!appointmentsLoading && pagedPastAppointments.totalItems === 0 ? <div className="empty-row">История записей пуста</div> : null}
            {!appointmentsLoading && pagedPastAppointments.items.map((item) => renderAppointmentItem(item, false))}
          </div>
          {!appointmentsLoading ? renderPagination("pastAppointments", pagedPastAppointments) : null}
        </div>

        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Платежи</span>
          </div>
          <div className="panel-card-body panel-card-body--rows">
            {appointmentsLoading ? <div className="empty-row">Загрузка...</div> : null}
            {!appointmentsLoading && pagedPayments.totalItems === 0 ? <div className="empty-row">Платежей пока нет</div> : null}
            {!appointmentsLoading && pagedPayments.items.map((payment) => (
              <div className="payment-row" key={`pay-${payment.id}-${payment.createdAt || "na"}`}>
                <div className="payment-info">
                  <div className="payment-desc">{payment.description}</div>
                  <div className="payment-date">{formatDateTime(payment.createdAt)}{payment.id ? ` · ID: ${payment.id}` : ""}</div>
                </div>
                <span className={`payment-status ${activeBadgeClass(payment.status)}`}>{payment.statusLabel}</span>
                <div className="payment-amount">{formatPrice(payment.amount)}</div>
              </div>
            ))}
          </div>
          {!appointmentsLoading ? renderPagination("payments", pagedPayments) : null}
        </div>
      </div>

      <div className="right-col">
        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Итого расходов</span>
          </div>
          <div className="panel-card-body">
            <div className="spend-total">
              <div className="spend-total__value">{formatPrice(totalSpent)}</div>
              <div className="spend-total__sub">за всё время · {payments.length} платежей</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPersonList = (items, emptyText, options = {}) => {
    const {
      actionLabel = "",
      onAction = null,
      busyId = "",
      showOpen = false,
      resolveItemTo = () => "",
    } = options;
    if (items.length === 0) return <div className="empty-row">{emptyText}</div>;
    return items.map((item) => {
      const openTo = showOpen ? resolveItemTo(item) : "";
      return (
        <div
          className={`sub-item ${showOpen || typeof onAction === "function" ? "sub-item--with-action" : ""}`}
          key={`${item.id}-${item.name}`}
        >
          <div className="sub-avatar">{profileInitials(item.name)}</div>
          <div className="sub-body">
            <div className="sub-name">{item.name}</div>
            {item.subtitle ? <div className="sub-type">{item.subtitle}</div> : null}
          </div>
          {showOpen || typeof onAction === "function" ? (
            <div className="sub-actions">
              {showOpen ? (
                <Link className="btn-sm" to={openTo || "/catalog"}>
                  Открыть
                </Link>
              ) : null}
              {typeof onAction === "function" ? (
                <button
                  type="button"
                  className="btn-sm danger settings-sub-action"
                  onClick={() => onAction(item)}
                  disabled={!item.id || busyId === String(item.id)}
                >
                  {busyId === String(item.id) ? "Подождите..." : actionLabel}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      );
    });
  };

  const renderDiscoveryList = (items, config = {}) => {
    const {
      title = "",
      note = "",
      browseTo = "/",
      browseLabel = "Открыть",
      emptyTitle = "",
      emptyNote = "",
      emptyActionLabel = "Открыть каталог",
      actionLabel = "",
      onAction = null,
      busyId = "",
      resolveItemTo = () => "/",
      resolveSubtitle = () => "",
      resolveBadge = () => "•",
    } = config;

    if (!items.length) {
      return (
        <div className="settings-discovery settings-discovery--empty">
          <div className="settings-discovery__copy">
            <div className="settings-discovery__title">{emptyTitle}</div>
            <div className="settings-discovery__note">{emptyNote}</div>
          </div>
          <Link className="btn-sm primary" to={browseTo}>{emptyActionLabel}</Link>
        </div>
      );
    }

    return (
      <>
        <div className="settings-discovery">
          <div className="settings-discovery__copy">
            <div className="settings-discovery__title">{title}</div>
            <div className="settings-discovery__note">{note}</div>
          </div>
          <Link className="btn-sm" to={browseTo}>{browseLabel}</Link>
        </div>
        {items.map((item) => (
          <div className="sub-item sub-item--with-action sub-item--discovery" key={`discover-${item.id || item.routeId || item.title}`}>
            <div className="sub-avatar">{resolveBadge(item)}</div>
            <div className="sub-body">
              <div className="sub-name">{item.title}</div>
              <div className="sub-type">{resolveSubtitle(item)}</div>
            </div>
            <div className="sub-actions sub-actions--stack">
              <Link className="btn-sm" to={resolveItemTo(item) || browseTo}>Открыть</Link>
              <button
                type="button"
                className="btn-sm primary settings-sub-action"
                onClick={() => onAction?.(item)}
                disabled={!item.id || busyId === String(item.id)}
              >
                {busyId === String(item.id) ? "Подождите..." : actionLabel}
              </button>
            </div>
          </div>
        ))}
      </>
    );
  };

  const renderPagination = (key, pageData) => {
    if (!pageData || pageData.totalItems === 0) return null;

    if (pageData.totalPages <= 1) {
      return (
        <div className="panel-card-footer panel-card-footer--compact">
          <div className="pager__summary">Показано {pageData.startIndex}-{pageData.endIndex} из {pageData.totalItems}</div>
        </div>
      );
    }

    const tokens = buildPaginationTokens(pageData.totalPages, pageData.currentPage);

    return (
      <div className="panel-card-footer">
        <div className="pager__summary">Показано {pageData.startIndex}-{pageData.endIndex} из {pageData.totalItems}</div>
        <div className="pager">
          <button
            type="button"
            className="pager__btn"
            onClick={() => setListPage(key, pageData.currentPage - 1)}
            disabled={pageData.currentPage <= 1}
            aria-label="Предыдущая страница"
          >
            ←
          </button>
          {tokens.map((token) =>
            typeof token === "number" ? (
              <button
                key={`${key}-${token}`}
                type="button"
                className={`pager__btn ${pageData.currentPage === token ? "is-active" : ""}`}
                onClick={() => setListPage(key, token)}
              >
                {token}
              </button>
            ) : (
              <span key={`${key}-${token}`} className="pager__ellipsis">…</span>
            )
          )}
          <button
            type="button"
            className="pager__btn"
            onClick={() => setListPage(key, pageData.currentPage + 1)}
            disabled={pageData.currentPage >= pageData.totalPages}
            aria-label="Следующая страница"
          >
            →
          </button>
        </div>
      </div>
    );
  };

  const renderTabSubscriptions = () => (
    <div className="tab-panel tab-panel--subscriptions tab-panel--single-col active" id="tab-subscriptions">
      <div className="left-col">
        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Подписки на психологов</span>
            <span className="meta-small">{psychSubscriptions.length} психологов</span>
          </div>
          <div className="panel-card-body panel-card-body--rows">
            {subscriptionsLoading ? (
              <div className="empty-row">Загрузка...</div>
            ) : (
              renderPersonList(pagedPsychSubscriptions.items, "Подписок пока нет", {
                actionLabel: "Отписаться",
                onAction: handleUnsubscribePsychologist,
                busyId: unsubscribingPsychId,
                showOpen: true,
                resolveItemTo: (item) => (item?.id ? `/psychologist/${encodeURIComponent(String(item.id))}` : ""),
              })
            )}
          </div>
          {!subscriptionsLoading ? renderPagination("psychSubscriptions", pagedPsychSubscriptions) : null}
        </div>

        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Избранные психологи</span>
            <span className="meta-small">{favorites.length} специалистов</span>
          </div>
          <div className="panel-card-body panel-card-body--rows">
            {subscriptionsLoading ? (
              <div className="empty-row">Загрузка...</div>
            ) : (
              renderPersonList(pagedFavorites.items, "Избранное пусто", {
                actionLabel: "Убрать",
                onAction: handleRemoveFavoritePsychologist,
                busyId: removingFavoriteId,
                showOpen: true,
                resolveItemTo: (item) => (item?.id ? `/psychologist/${encodeURIComponent(String(item.id))}` : ""),
              })
            )}
          </div>
          {!subscriptionsLoading ? renderPagination("favorites", pagedFavorites) : null}
        </div>

        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Подписки на авторов блога</span>
            <span className="meta-small">{blogAuthors.length} авторов</span>
          </div>
          <div className="panel-card-body panel-card-body--rows">
            {subscriptionsLoading ? (
              <div className="empty-row">Загрузка...</div>
            ) : (
              renderPersonList(pagedBlogAuthors.items, "Подписок на авторов нет", {
                actionLabel: "Отписаться",
                onAction: handleUnsubscribeBlogAuthor,
                busyId: unsubscribingBlogAuthorId,
                showOpen: true,
                resolveItemTo: (item) => (item?.id ? `/psychologist/${encodeURIComponent(String(item.id))}` : ""),
              })
            )}
          </div>
          {!subscriptionsLoading ? renderPagination("blogAuthors", pagedBlogAuthors) : null}
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => (
    <div className="tab-panel active" id="tab-content">
      <div className="left-col">
        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Мои темы на форуме</span>
            <span className="meta-small">{forumTopics.length} тем</span>
          </div>
          <div className="panel-card-body panel-card-body--rows">
            {contentLoading ? <div className="empty-row">Загрузка...</div> : null}
            {!contentLoading && pagedForumTopics.totalItems === 0 ? <div className="empty-row">Темы на форуме пока не созданы</div> : null}
            {!contentLoading &&
              pagedForumTopics.items.map((topic) => {
                const topicPath = resolveForumTopicPath(topic);
                return (
                  <Link className="content-item content-item--link" key={`topic-${topic.id}`} to={topicPath}>
                    <div className="content-main">
                      <div className="content-type-icon" style={{ background: "rgba(224,123,90,0.1)" }}>🗨️</div>
                      <div className="content-body">
                        <div className="content-title">{topic.title || "Тема"}</div>
                        <div className="content-meta">Форум · {topic.commentsCount || 0} ответов · {formatDate(topic.createdWhen)}</div>
                      </div>
                    </div>
                    <span className="content-open">Открыть</span>
                  </Link>
                );
              })}
          </div>
          {!contentLoading ? renderPagination("forumTopics", pagedForumTopics) : null}
        </div>

        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Мои посты в блоге</span>
            <span className="meta-small">{blogPosts.length} постов</span>
          </div>
          <div className="panel-card-body panel-card-body--rows">
            {contentLoading ? <div className="empty-row">Загрузка...</div> : null}
            {!contentLoading && pagedBlogPosts.totalItems === 0 ? <div className="empty-row">Постов в блоге пока нет</div> : null}
            {!contentLoading &&
              pagedBlogPosts.items.map((post) => (
                <Link
                  className="content-item content-item--link"
                  key={`post-${post.id || post.slug || post.title}`}
                  to={buildBlogPostPath(post)}
                >
                  <div className="content-main">
                    <div className="content-type-icon" style={{ background: "var(--lav-dim)" }}>📝</div>
                    <div className="content-body">
                      <div className="content-title">{post.title || "Пост"}</div>
                      <div className="content-meta">Блог · {formatDate(post.createdWhen || post.publishedAt)}</div>
                    </div>
                  </div>
                  <span className="content-open">Открыть</span>
                </Link>
              ))}
          </div>
          {!contentLoading ? renderPagination("blogPosts", pagedBlogPosts) : null}
        </div>

        <div className="panel-card">
          <div className="panel-card-header panel-card-header--wrap">
            <span className="panel-card-title">Мои комментарии</span>
            <div className="settings-chip-group" role="tablist" aria-label="Фильтр комментариев">
              {COMMENT_FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  className={`settings-chip ${commentFilter === filter.id ? "is-active" : ""}`}
                  onClick={() => setCommentFilter(filter.id)}
                  aria-pressed={commentFilter === filter.id}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
          <div className="panel-card-body panel-card-body--rows">
            {contentLoading ? <div className="empty-row">Загрузка...</div> : null}
            {!contentLoading && pagedComments.totalItems === 0 ? <div className="empty-row">Комментариев не найдено</div> : null}
            {!contentLoading &&
              pagedComments.items.map((comment, index) => {
                const commentPath = resolveCommentTargetPath(comment);
                const contextTitle = resolveCommentSourceTitle(comment);
                return (
                  <Link className="content-item content-item--link" key={`comment-${comment.id || index}`} to={commentPath}>
                    <div className="content-main">
                      <div
                        className="content-type-icon"
                        style={{
                          background:
                            comment.category === "forum"
                              ? "rgba(224,123,90,0.1)"
                              : comment.category === "podcast"
                                ? "rgba(90,138,234,0.1)"
                                : comment.category === "literature"
                                  ? "rgba(224,176,90,0.1)"
                                  : "var(--lav-dim)",
                        }}
                      >
                        {comment.category === "forum" ? "🗨️" : comment.category === "podcast" ? "🎙️" : comment.category === "literature" ? "📚" : "📝"}
                      </div>
                      <div className="content-body">
                        <div className="content-title">{comment.content || comment.text || "Комментарий"}</div>
                        {contextTitle ? <div className="content-context">{contextTitle}</div> : null}
                        <div className="content-meta">{contentCategoryLabel(comment.category)} · {formatDate(comment.createdWhen || comment.createdAt)}</div>
                      </div>
                    </div>
                    <span className="content-open">{contentOpenLabel(comment.category)}</span>
                  </Link>
                );
              })}
          </div>
          {!contentLoading ? renderPagination("comments", pagedComments) : null}
        </div>
      </div>

      <div className="right-col">
        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Активность</span>
          </div>
          <div className="panel-card-body panel-card-body--rows">
            <div className="field-row"><span className="field-key">Тем создано</span><span className="field-val right">{forumTopics.length}</span></div>
            <div className="field-row"><span className="field-key">Постов в блоге</span><span className="field-val right">{blogPosts.length}</span></div>
            <div className="field-row"><span className="field-key">Комментариев</span><span className="field-val right">{comments.length}</span></div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabPsychologist = () => {
    const selectedThemeKeys = new Set(
      asArray(psychologistSettingsForm.themes).map((item) => String(item?.id))
    );
    const selectedMethodKeys = new Set(
      asArray(psychologistSettingsForm.methods).map((item) => String(item?.id))
    );
    const certPreviewUrl = sanitizeLinkHref(activeCertificatePreview?.documentUrl || "", {
      allowRelative: true,
      allowMailto: false,
      allowTel: false,
      allowHash: false,
    });
    const certPreviewMediaType = detectDocumentMediaType(
      certPreviewUrl,
      activeCertificatePreview?.fileName || activeCertificatePreview?.title || "",
      activeCertificatePreview?.mimeType || ""
    );
    const certPreviewIsImage = certPreviewMediaType === "image";
    const certPreviewCanEmbedFrame = canEmbedDocumentFrame(certPreviewUrl);

    return (
      <div className="tab-panel active" id="tab-psychologist">
        <div className="left-col">
          <div className="panel-card" data-onboarding-target="psychologist-profile-fields">
            <div className="panel-card-header">
              <div className="panel-card-title-wrap">
                <span className="panel-card-title">Профиль психолога</span>
                <span className={`badge-inline ${psychologistDirtyBySection.profile ? "badge-inline--dirty" : ""}`}>
                  {psychologistDirtyBySection.profile ? "Не сохранено" : "Сохранено"}
                </span>
              </div>
              <div className="btn-group-inline">
                <button
                  className="btn-sm"
                  type="button"
                  onClick={loadPsychologistSettings}
                  disabled={psychologistSettingsLoading || psychologistSettingsSaving}
                >
                  Обновить
                </button>
                <button
                  className="btn-sm primary"
                  type="button"
                  onClick={handleSavePsychologistSettings}
                  disabled={
                    psychologistSettingsLoading ||
                    psychologistSettingsSaving ||
                    !psychologistDirtyBySection.any
                  }
                >
                  {psychologistSettingsSaving ? "Сохраняем..." : "Сохранить"}
                </button>
              </div>
            </div>
            <div className="panel-card-body">
              {psychologistSettingsLoading ? (
                <div className="empty-row">Загружаем настройки психолога...</div>
              ) : (
                <div className="psy-settings">
                  <div className="psy-settings__group" data-onboarding-target="psychologist-topics-field">
                    <div className="form-label">Темы (из справочника)</div>
                    {!psychologistThemeOptions.length ? (
                      <div className="empty-row">Справочник тем пока недоступен</div>
                    ) : (
                      <div className="psy-settings__chips">
                        {psychologistThemeOptions.map((option) => (
                          <button
                            key={`theme-${String(option.id)}`}
                            type="button"
                            className={`psy-chip ${
                              selectedThemeKeys.has(String(option.id)) ? "is-active" : ""
                            }`}
                            onClick={() =>
                              togglePsychologistDictionaryValue("themes", option)
                            }
                            disabled={
                              psychologistSettingsSaving || option.isActive === false
                            }
                          >
                            {option.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="psy-settings__group" data-onboarding-target="psychologist-methods-field">
                    <div className="form-label">Методы (из справочника)</div>
                    {!psychologistMethodOptions.length ? (
                      <div className="empty-row">Справочник методов пока недоступен</div>
                    ) : (
                      <div className="psy-settings__chips">
                        {psychologistMethodOptions.map((option) => (
                          <button
                            key={`method-${String(option.id)}`}
                            type="button"
                            className={`psy-chip ${
                              selectedMethodKeys.has(String(option.id)) ? "is-active" : ""
                            }`}
                            onClick={() =>
                              togglePsychologistDictionaryValue("methods", option)
                            }
                            disabled={
                              psychologistSettingsSaving || option.isActive === false
                            }
                          >
                            {option.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="panel-card" data-onboarding-target="psychologist-work-fields">
            <div className="panel-card-header">
              <div className="panel-card-title-wrap">
                <span className="panel-card-title">Опыт, цена и формат работы</span>
                <span className={`badge-inline ${psychologistDirtyBySection.work ? "badge-inline--dirty" : ""}`}>
                  {psychologistDirtyBySection.work ? "Не сохранено" : "Сохранено"}
                </span>
              </div>
              <div className="btn-group-inline">
                <button
                  className="btn-sm primary"
                  type="button"
                  onClick={handleSavePsychologistSettings}
                  disabled={
                    psychologistSettingsLoading ||
                    psychologistSettingsSaving ||
                    !psychologistDirtyBySection.work
                  }
                >
                  {psychologistSettingsSaving ? "Сохраняем..." : "Сохранить раздел"}
                </button>
              </div>
            </div>
            <div className="panel-card-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Опыт работы (лет)</label>
                  <input
                    className="form-input"
                    type="number"
                    min="0"
                    value={psychologistSettingsForm.experienceYears}
                    onChange={(event) =>
                      updatePsychologistSettingsField(
                        "experienceYears",
                        event.target.value
                      )
                    }
                    placeholder="Например, 7"
                    disabled={psychologistSettingsSaving}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Цена за сессию (₽)</label>
                  <input
                    className="form-input"
                    data-onboarding-target="psychologist-price-field"
                    type="number"
                    min="0"
                    value={psychologistSettingsForm.pricePerSession}
                    onChange={(event) =>
                      updatePsychologistSettingsField(
                        "pricePerSession",
                        event.target.value
                      )
                    }
                    placeholder="Например, 3500"
                    disabled={psychologistSettingsSaving}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">О себе</label>
                <textarea
                  className="form-input form-input--textarea"
                  data-onboarding-target="psychologist-about-field"
                  rows={5}
                  value={psychologistSettingsForm.about}
                  onChange={(event) =>
                    updatePsychologistSettingsField("about", event.target.value)
                  }
                  placeholder="Коротко опишите подход, с чем помогаете, кому подходите."
                  disabled={psychologistSettingsSaving}
                />
              </div>

              <div className="psy-settings__formats" data-onboarding-target="psychologist-format-field">
                <label className="psy-format-check">
                  <input
                    type="checkbox"
                    checked={Boolean(psychologistSettingsForm.workFormatOnline)}
                    onChange={(event) =>
                      updatePsychologistSettingsField(
                        "workFormatOnline",
                        event.target.checked
                      )
                    }
                    disabled={psychologistSettingsSaving}
                  />
                  <span>Провожу онлайн-сессии</span>
                </label>
                <label className="psy-format-check">
                  <input
                    type="checkbox"
                    checked={Boolean(psychologistSettingsForm.workFormatOffline)}
                    onChange={(event) =>
                      updatePsychologistSettingsField(
                        "workFormatOffline",
                        event.target.checked
                      )
                    }
                    disabled={psychologistSettingsSaving}
                  />
                  <span>Провожу оффлайн-встречи</span>
                </label>
              </div>
            </div>
          </div>

          <div className="panel-card">
            <div className="panel-card-header">
              <div className="panel-card-title-wrap">
                <span className="panel-card-title">Образование и связь</span>
                <span className={`badge-inline ${psychologistDirtyBySection.educationContacts ? "badge-inline--dirty" : ""}`}>
                  {psychologistDirtyBySection.educationContacts ? "Не сохранено" : "Сохранено"}
                </span>
              </div>
              <div className="btn-group-inline">
                <button
                  className="btn-sm"
                  type="button"
                  onClick={handlePsychologistEducationAdd}
                  disabled={psychologistSettingsSaving}
                >
                  Добавить образование
                </button>
                <button
                  className="btn-sm primary"
                  type="button"
                  onClick={handleSavePsychologistSettings}
                  disabled={
                    psychologistSettingsLoading ||
                    psychologistSettingsSaving ||
                    !psychologistDirtyBySection.educationContacts
                  }
                >
                  {psychologistSettingsSaving ? "Сохраняем..." : "Сохранить раздел"}
                </button>
              </div>
            </div>
            <div className="panel-card-body">
              <div className="psy-settings__notice">
                Документы об образовании обрабатываются оператором персональных данных — ИП Козырева Валерия Витальевна, владельцем сервиса «Бюро Души», только для внутренней проверки профиля психолога. Документы не публикуются в открытом доступе.
              </div>
              {!asArray(psychologistSettingsForm.education).length ? (
                <div className="empty-row">Образование пока не добавлено</div>
              ) : (
                asArray(psychologistSettingsForm.education).map((row, index) => (
                  <div className="psy-settings__nested-card" key={`edu-${row.id || index}`}>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Учебное заведение</label>
                        <input
                          className="form-input"
                          type="text"
                          value={row.institution || ""}
                          onChange={(event) =>
                            handlePsychologistEducationChange(
                              index,
                              "institution",
                              event.target.value
                            )
                          }
                          placeholder="Например, МГППУ"
                          disabled={psychologistSettingsSaving}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Специализация</label>
                        <input
                          className="form-input"
                          type="text"
                          value={row.specialization || ""}
                          onChange={(event) =>
                            handlePsychologistEducationChange(
                              index,
                              "specialization",
                              event.target.value
                            )
                          }
                          placeholder="Психологическое консультирование"
                          disabled={psychologistSettingsSaving}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Документ об образовании</label>
                      {row.fileUrl ? (
                        <div className="file-uploaded">
                          <span className="file-uploaded__icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                          </span>
                          <span className="file-uploaded__name">{row.fileName || "Загруженный файл"}</span>
                          <div className="btn-group-inline">
                            {row.fileUrl && (
                              <a
                                className="btn-sm"
                                href={row.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Открыть
                              </a>
                            )}
                            <button
                              className="btn-sm danger"
                              type="button"
                              onClick={() =>
                                setPsychologistSettingsForm((prev) => ({
                                  ...prev,
                                  education: asArray(prev.education).map((r, i) =>
                                    i === index ? { ...r, fileUrl: "", fileName: "" } : r
                                  ),
                                }))
                              }
                              disabled={psychologistSettingsSaving}
                            >
                              Удалить файл
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="file-upload-area">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            className="file-upload-area__input"
                            id={`edu-upload-${index}`}
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              if (file) handleEducationFileUpload(index, file);
                              event.target.value = "";
                            }}
                            disabled={psychologistSettingsSaving || uploadingEducationFile?.index === index}
                          />
                          <label
                            htmlFor={`edu-upload-${index}`}
                            className={`file-upload-area__label ${
                              uploadingEducationFile?.index === index ? "is-uploading" : ""
                            }`}
                          >
                            {uploadingEducationFile?.index === index ? (
                              <>
                                <span className="spinner" />
                                Загружаем {uploadingEducationFile.fileName}...
                              </>
                            ) : (
                              <>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                  <polyline points="17 8 12 3 7 8" />
                                  <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                                Загрузить документ
                              </>
                            )}
                          </label>
                          <span className="file-upload-area__hint">PDF, JPG, PNG, DOC до 10 МБ</span>
                        </div>
                      )}
                    </div>
                    <div className="psy-settings__nested-actions">
                      <div className="verification-status">
                        {row.verified || row.verificationStatus === "VERIFIED" ? (
                          <span className="verification-badge verification-badge--verified">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Подтверждено
                          </span>
                        ) : row.fileUrl ? (
                          <span className="verification-badge verification-badge--pending">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="12" y1="8" x2="12" y2="12" />
                              <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            На проверке
                          </span>
                        ) : (
                          <span className="verification-badge verification-badge--empty">
                            Документ не загружен
                          </span>
                        )}
                      </div>
                      <div className="btn-group-inline">
                        <button
                          className="btn-sm danger"
                          type="button"
                          onClick={() => handlePsychologistEducationRemove(index)}
                          disabled={psychologistSettingsSaving}
                        >
                          Удалить
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}

              <div className="psy-settings__contacts">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      className="form-input"
                      type="email"
                      value={psychologistSettingsForm.publicEmail}
                      onChange={(event) =>
                        updatePsychologistSettingsField(
                          "publicEmail",
                          event.target.value
                        )
                      }
                      placeholder="psy@example.com"
                      disabled={psychologistSettingsSaving}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Телефон</label>
                    <input
                      className="form-input"
                      type="text"
                      value={psychologistSettingsForm.contactPhone}
                      onChange={(event) =>
                        updatePsychologistSettingsField(
                          "contactPhone",
                          event.target.value
                        )
                      }
                      placeholder="+79990000041"
                      disabled={psychologistSettingsSaving}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Регион</label>
                  <input
                    className="form-input"
                    type="text"
                    value={psychologistSettingsForm.regionLabel}
                    onChange={(event) =>
                      updatePsychologistSettingsField(
                        "regionLabel",
                        event.target.value
                      )
                    }
                    placeholder="Екатеринбург"
                    disabled={psychologistSettingsSaving}
                  />
                </div>
                <div className="psy-settings__socials">
                  <div className="psy-settings__socials-head">
                    <label className="form-label">Соцсети и ссылки</label>
                    <button
                      className="btn-sm"
                      type="button"
                      onClick={handleSocialLinkAdd}
                      disabled={psychologistSettingsSaving}
                    >
                      + Добавить соцсеть
                    </button>
                  </div>
                  {!asArray(psychologistSettingsForm.socialLinks).length ? (
                    <div className="empty-row">Добавьте Telegram, Instagram, VK и другие ссылки</div>
                  ) : (
                    asArray(psychologistSettingsForm.socialLinks).map((row, index) => {
                      const option = socialOptionByType(row.type);
                      return (
                        <div className="psy-social-row" key={`social-${row.id || index}`}>
                          <div className={`psy-social-row__icon psy-social-row__icon--${option.type}`}>
                            <span>{option.icon}</span>
                          </div>
                          <select
                            className="form-input"
                            value={row.type || "telegram"}
                            onChange={(event) =>
                              handleSocialLinkChange(index, "type", event.target.value)
                            }
                            disabled={psychologistSettingsSaving}
                          >
                            {SOCIAL_NETWORK_OPTIONS.map((item) => (
                              <option key={item.type} value={item.type}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                          <input
                            className="form-input"
                            type="text"
                            value={row.url || ""}
                            onChange={(event) =>
                              handleSocialLinkChange(index, "url", event.target.value)
                            }
                            placeholder={option.placeholder}
                            disabled={psychologistSettingsSaving}
                          />
                          <button
                            className="btn-sm danger"
                            type="button"
                            onClick={() => handleSocialLinkRemove(index)}
                            disabled={psychologistSettingsSaving}
                          >
                            Удалить
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="panel-card">
            <div className="panel-card-header">
              <div className="panel-card-title-wrap">
                <span className="panel-card-title">Сертификаты</span>
                <span className={`badge-inline ${psychologistDirtyBySection.certificates ? "badge-inline--dirty" : ""}`}>
                  {psychologistDirtyBySection.certificates ? "Не сохранено" : "Сохранено"}
                </span>
              </div>
              <div className="btn-group-inline">
                <button
                  className="btn-sm"
                  type="button"
                  onClick={handlePsychologistCertificateAdd}
                  disabled={psychologistSettingsSaving}
                >
                  Добавить сертификат
                </button>
                <button
                  className="btn-sm primary"
                  type="button"
                  onClick={handleSavePsychologistSettings}
                  disabled={
                    psychologistSettingsLoading ||
                    psychologistSettingsSaving ||
                    !psychologistDirtyBySection.certificates
                  }
                >
                  {psychologistSettingsSaving ? "Сохраняем..." : "Сохранить раздел"}
                </button>
              </div>
            </div>
            <div className="panel-card-body">
              <div className="psy-settings__notice">
                ✓ Все дипломы проверены платформой и подтверждены оригиналами документов
              </div>
              {!asArray(psychologistSettingsForm.certificates).length ? (
                <div className="empty-row">Сертификаты пока не добавлены</div>
              ) : (
                asArray(psychologistSettingsForm.certificates).map((row, index) => (
                  <div
                    className="psy-settings__nested-card"
                    key={`cert-${row.id || index}`}
                  >
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Название</label>
                        <input
                          className="form-input"
                          type="text"
                          value={row.title || ""}
                          onChange={(event) =>
                            handlePsychologistCertificateChange(
                              index,
                              "title",
                              event.target.value
                            )
                          }
                          placeholder="КПТ-практик"
                          disabled={psychologistSettingsSaving}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Организация</label>
                        <input
                          className="form-input"
                          type="text"
                          value={row.issuer || ""}
                          onChange={(event) =>
                            handlePsychologistCertificateChange(
                              index,
                              "issuer",
                              event.target.value
                            )
                          }
                          placeholder="Ассоциация КПТ"
                          disabled={psychologistSettingsSaving}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Дата выдачи</label>
                        <input
                          className="form-input"
                          type="text"
                          value={row.issuedAt || ""}
                          onChange={(event) =>
                            handlePsychologistCertificateChange(
                              index,
                              "issuedAt",
                              event.target.value
                            )
                          }
                          placeholder="06 апреля 2024"
                          disabled={psychologistSettingsSaving}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Документ (скан/фото диплома)</label>
                        {row.documentUrl ? (
                          <div className="file-uploaded">
                            <span className="file-uploaded__icon">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                              </svg>
                            </span>
                            <span className="file-uploaded__name">{row.fileName || "Загруженный файл"}</span>
                            <div className="btn-group-inline">
                              <button
                                className="btn-sm"
                                type="button"
                                onClick={() => setActiveCertificatePreview(row)}
                              >
                                Открыть
                              </button>
                              <button
                                className="btn-sm danger"
                                type="button"
                                onClick={() =>
                                  setPsychologistSettingsForm((prev) => ({
                                    ...prev,
                                    certificates: asArray(prev.certificates).map((r, i) =>
                                      i === index ? { ...r, documentUrl: "", fileName: "" } : r
                                    ),
                                  }))
                                }
                                disabled={psychologistSettingsSaving}
                              >
                                Удалить файл
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="file-upload-area">
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                              className="file-upload-area__input"
                              id={`cert-upload-${index}`}
                              onChange={(event) => {
                                const file = event.target.files?.[0];
                                if (file) handleCertificateFileUpload(index, file);
                                event.target.value = "";
                              }}
                              disabled={psychologistSettingsSaving || uploadingCertificateFile?.index === index}
                            />
                            <label
                              htmlFor={`cert-upload-${index}`}
                              className={`file-upload-area__label ${
                                uploadingCertificateFile?.index === index ? "is-uploading" : ""
                              }`}
                            >
                              {uploadingCertificateFile?.index === index ? (
                                <>
                                  <span className="spinner" />
                                  Загружаем {uploadingCertificateFile.fileName}...
                                </>
                              ) : (
                                <>
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                  </svg>
                                  Загрузить документ
                                </>
                              )}
                            </label>
                            <span className="file-upload-area__hint">PDF, JPG, PNG, DOC до 10 МБ</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="psy-settings__nested-actions">
                      <div className="verification-status">
                        {row.verified || row.verificationStatus === "VERIFIED" ? (
                          <span className="verification-badge verification-badge--verified">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Подтверждено
                          </span>
                        ) : row.documentUrl ? (
                          <span className="verification-badge verification-badge--pending">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="12" y1="8" x2="12" y2="12" />
                              <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            На проверке
                          </span>
                        ) : (
                          <span className="verification-badge verification-badge--empty">
                            Документ не загружен
                          </span>
                        )}
                      </div>
                      <div className="btn-group-inline">
                        <button
                          className="btn-sm danger"
                          type="button"
                          onClick={() => handlePsychologistCertificateRemove(index)}
                          disabled={psychologistSettingsSaving}
                        >
                          Удалить
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="right-col">
          <div className="panel-card">
            <div className="panel-card-header">
              <span className="panel-card-title">Сводка профиля</span>
            </div>
            <div className="panel-card-body panel-card-body--rows">
              <div className="field-row">
                <span className="field-key">Темы</span>
                <span className="field-val right">
                  {asArray(psychologistSettingsForm.themes).length}
                </span>
              </div>
              <div className="field-row">
                <span className="field-key">Методы</span>
                <span className="field-val right">
                  {asArray(psychologistSettingsForm.methods).length}
                </span>
              </div>
              <div className="field-row">
                <span className="field-key">Образование</span>
                <span className="field-val right">
                  {asArray(psychologistSettingsForm.education).length}
                </span>
              </div>
              <div className="field-row">
                <span className="field-key">Сертификаты</span>
                <span className="field-val right">
                  {asArray(psychologistSettingsForm.certificates).length}
                </span>
              </div>
              <div className="field-row">
                <span className="field-key">Формат</span>
                <span className="field-val right">
                  {psychologistSettingsForm.workFormatOnline ? "Онлайн" : ""}
                  {psychologistSettingsForm.workFormatOnline &&
                  psychologistSettingsForm.workFormatOffline
                    ? " + "
                    : ""}
                  {psychologistSettingsForm.workFormatOffline ? "Оффлайн" : ""}
                  {!psychologistSettingsForm.workFormatOnline &&
                  !psychologistSettingsForm.workFormatOffline
                    ? "Не выбран"
                    : ""}
                </span>
              </div>
            </div>
          </div>

          <div className="panel-card">
            <div className="panel-card-header">
              <div className="panel-card-title-wrap">
                <span className="panel-card-title">Публикация</span>
                <span className={`badge-inline ${psychologistDirtyBySection.any ? "badge-inline--dirty" : ""}`}>
                  {psychologistDirtyBySection.any ? "Есть несохранённые изменения" : "Все изменения сохранены"}
                </span>
              </div>
            </div>
            <div className="panel-card-body">
              <div className="form-hint">
                Изменения сразу отображаются в публичном профиле психолога,
                в карточках поиска и на странице записи.
              </div>
              <div className="action-right">
                <button
                  className="btn-sm primary"
                  type="button"
                  onClick={handleSavePsychologistSettings}
                  disabled={
                    psychologistSettingsLoading ||
                    psychologistSettingsSaving ||
                    !psychologistDirtyBySection.any
                  }
                >
                  {psychologistSettingsSaving ? "Сохраняем..." : "Опубликовать"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {activeCertificatePreview ? (
          <div className="psy-cert-modal" role="dialog" aria-modal="true">
            <button
              className="psy-cert-modal__backdrop"
              type="button"
              aria-label="Закрыть просмотр сертификата"
              onClick={() => setActiveCertificatePreview(null)}
            />
            <div className="psy-cert-modal__card">
              <div className="psy-cert-modal__head">
                <div className="psy-cert-modal__title">
                  {activeCertificatePreview.title || "Сертификат"}
                </div>
                <button
                  className="psy-cert-modal__close"
                  type="button"
                  onClick={() => setActiveCertificatePreview(null)}
                >
                  ×
                </button>
              </div>
              <div className="psy-cert-modal__body">
                {certPreviewUrl ? (
                  certPreviewIsImage ? (
                    <img
                      src={certPreviewUrl}
                      alt={activeCertificatePreview.title || "Сертификат"}
                      className="psy-cert-modal__media"
                    />
                  ) : (
                    <>
                      {certPreviewCanEmbedFrame ? (
                        <iframe
                          src={certPreviewUrl}
                          title={activeCertificatePreview.title || "Сертификат"}
                          className="psy-cert-modal__media"
                        />
                      ) : (
                        <div className="psy-cert-modal__fallback">
                          Предпросмотр недоступен из-за политики безопасности источника. Откройте документ в новой вкладке.
                        </div>
                      )}
                      <a
                        className="btn-sm primary"
                        href={certPreviewUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        style={{ marginTop: 12, alignSelf: "center" }}
                      >
                        Открыть документ
                      </a>
                    </>
                  )
                ) : (
                  <div className="empty-row">Ссылка на документ не указана.</div>
                )}
              </div>
              <div className="psy-cert-modal__foot">Оригинал у специалиста</div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderTabIntegrations = () => {
    const googleConnected = Boolean(googleCalendarStatus?.connected);
    const googleEnabled = Boolean(googleCalendarStatus?.enabled);
    const googleConfigured = Boolean(googleCalendarStatus?.configured);
    const googleStatusLabel = !googleEnabled
      ? "Выключено"
      : !googleConfigured
      ? "Не настроено на сервере"
      : googleConnected
      ? "Подключено"
      : "Ожидает подключения";
    const googleStatusTone = googleConnected
      ? "is-success"
      : !googleEnabled || !googleConfigured
      ? "is-muted"
      : "is-pending";
    const googleHeroTitle = googleConnected ? "Google Calendar подключён" : "Подключите Google Calendar";
    const googleHeroText = !googleEnabled
      ? "Интеграция сейчас выключена. После включения на сервере здесь появится подключение аккаунта и параметры синхронизации."
      : !googleConfigured
      ? "Сервер ещё не настроен для Google Calendar. После настройки здесь можно будет подключить аккаунт и управлять синхронизацией."
      : googleConnected
      ? "События и занятость можно синхронизировать с вашим календарём. Ниже доступны данные аккаунта и параметры обмена."
      : "Подключите рабочий календарь, чтобы синхронизировать расписание, записи и занятость без ручного переноса.";
    const yandexConnected = Boolean(yandexCalendarStatus?.connected);
    const yandexEnabled = Boolean(yandexCalendarStatus?.enabled);
    const yandexConfigured = Boolean(yandexCalendarStatus?.configured);
    const yandexStatusLabel = !yandexEnabled
      ? "Выключено"
      : !yandexConfigured
      ? "Не настроено на сервере"
      : yandexConnected
      ? "Подключено"
      : "Ожидает подключения";
    const yandexStatusTone = yandexConnected
      ? "is-success"
      : !yandexEnabled || !yandexConfigured
      ? "is-muted"
      : "is-pending";
    const yandexHeroTitle = yandexConnected ? "Яндекс Календарь подключён" : "Подключите Яндекс Календарь";
    const yandexHeroText = !yandexEnabled
      ? "Интеграция сейчас выключена. После включения на сервере здесь появится подключение аккаунта и параметры синхронизации."
      : !yandexConfigured
      ? "Сервер ещё не настроен для Яндекс Календаря. После настройки здесь можно будет подключить аккаунт и управлять синхронизацией."
      : yandexConnected
      ? "События и занятость можно синхронизировать с Яндекс Календарём. Ниже доступны данные аккаунта и параметры обмена."
      : "Подключите Яндекс Календарь через авторизацию Яндекса, чтобы синхронизировать расписание и занятость без передачи пароля в интерфейсе.";

    return (
      <div className="tab-panel active" id="tab-integrations">
        <div className="left-col">
          <div className="panel-card">
            <div className="panel-card-header">
              <span className="panel-card-title">Google Calendar</span>
              <span className={`google-integration__header-status ${googleStatusTone}`}>
                {googleCalendarLoading ? "Загрузка..." : googleConnected ? "Подключено" : "Не подключено"}
              </span>
            </div>
            <div className="panel-card-body google-integration">
              <div className={`google-integration__hero ${googleStatusTone}`}>
                <div className="google-integration__hero-main">
                  <span className={`google-integration__badge ${googleStatusTone}`}>{googleStatusLabel}</span>
                  <h3 className="google-integration__title">{googleHeroTitle}</h3>
                  <p className="google-integration__text">{googleHeroText}</p>
                  {!googleConnected ? (
                    <CalendarConsentCard
                      checked={googleCalendarConsentChecked}
                      disabled={googleCalendarActionLoading || !googleEnabled || !googleConfigured}
                      text={GOOGLE_CALENDAR_CONSENT_TEXT}
                      version={GOOGLE_CALENDAR_CONSENT_VERSION}
                      onToggle={setGoogleCalendarConsentChecked}
                    />
                  ) : null}
                </div>

                <div className="google-integration__hero-actions">
                  {googleConnected ? (
                    <>
                      <button
                        className="btn-sm"
                        type="button"
                        onClick={() => setGoogleSyncConfiguratorOpen((prev) => !prev)}
                        disabled={googleCalendarSyncing || googleCalendarActionLoading}
                      >
                        {googleSyncConfiguratorOpen ? "Скрыть параметры" : "Параметры синхронизации"}
                      </button>
                      <button
                        className="btn-sm danger"
                        type="button"
                        onClick={handleDisconnectGoogleCalendar}
                        disabled={googleCalendarActionLoading || googleCalendarSyncing}
                      >
                        Отключить
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn-sm"
                      type="button"
                      onClick={handleConnectGoogleCalendar}
                      disabled={
                        googleCalendarActionLoading ||
                        !googleCalendarConsentChecked ||
                        !googleEnabled ||
                        !googleConfigured
                      }
                    >
                      {googleCalendarActionLoading ? "Подождите..." : "Подключить Google Calendar"}
                    </button>
                  )}
                </div>
              </div>

              <div className="google-integration__stats">
                <div className="google-integration__stat-card">
                  <span className="google-integration__stat-label">Статус</span>
                  <strong className="google-integration__stat-value">{googleStatusLabel}</strong>
                </div>
                <div className="google-integration__stat-card">
                  <span className="google-integration__stat-label">Аккаунт</span>
                  <strong className="google-integration__stat-value">
                    {googleCalendarStatus?.accountEmail || "Пока не выбран"}
                  </strong>
                </div>
                <div className="google-integration__stat-card">
                  <span className="google-integration__stat-label">Последняя синхронизация</span>
                  <strong className="google-integration__stat-value">
                    {googleCalendarStatus?.lastSyncedAt
                      ? formatDateTime(googleCalendarStatus.lastSyncedAt)
                      : "Ещё не запускалась"}
                  </strong>
                </div>
              </div>

              {googleCalendarStatus?.lastSyncMessage ? (
                <div className="google-integration__notice">
                  {googleCalendarStatus.lastSyncMessage}
                </div>
              ) : null}

              {googleConnected ? (
                <div className="google-integration__notice">
                  Бюро Души остаётся главным источником данных по сессиям: записи из Бюро Души создаются и обновляются в Google Calendar, а личные события из Google подтягиваются только как занятое время.
                </div>
              ) : null}

              {googleConnected && googleSyncConfiguratorOpen ? (
                <div className="google-sync-panel">
                  <div className="google-sync-panel__head">
                    <div>
                      <h4>Параметры синхронизации</h4>
                      <p>Настройте направление обмена и диапазон календарных событий.</p>
                    </div>
                  </div>

                  <div className="google-sync-panel__grid">
                    <label className="google-sync-panel__field">
                      <span className="google-sync-panel__label">Режим</span>
                      <select
                        className="google-sync-panel__control"
                        value={googleSyncOptions.direction}
                        onChange={(e) => handleGoogleSyncOptionChange("direction", e.target.value)}
                        disabled={googleCalendarSyncing || googleCalendarActionLoading}
                      >
                        {GOOGLE_SYNC_DIRECTION_OPTIONS.map((item) => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </select>
                    </label>

                    {googleSyncOptions.direction !== "TO_GOOGLE" ? (
                      <label className="google-sync-panel__field">
                        <span className="google-sync-panel__label">Конфликты</span>
                        <select
                          className="google-sync-panel__control"
                          value={googleSyncOptions.conflictPolicy}
                          onChange={(e) => handleGoogleSyncOptionChange("conflictPolicy", e.target.value)}
                          disabled={googleCalendarSyncing || googleCalendarActionLoading}
                        >
                          {GOOGLE_SYNC_CONFLICT_OPTIONS.map((item) => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                  </div>

                  <div className="google-sync-panel__range">
                    <span className="google-sync-panel__label">Куда добавлять события при бронировании</span>
                    <label className="google-sync-panel__field">
                      <span className="google-sync-panel__label">Календарь по умолчанию</span>
                      <select
                        className="google-sync-panel__control"
                        value={googleSyncOptions.exportCalendarId}
                        onChange={(e) => handleGoogleSyncOptionChange("exportCalendarId", e.target.value)}
                        disabled={googleCalendarSyncing || googleCalendarActionLoading}
                      >
                        {(googleCalendarStatus?.calendars || []).map((item) => (
                          <option key={item.id} value={item.id}>{item.label}</option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {(googleCalendarStatus?.calendars || []).length ? (
                    <div className="google-sync-panel__range">
                      <span className="google-sync-panel__label">Какие календари учитывать как занятость</span>
                      <div className="google-sync-panel__calendar-list">
                        {(googleCalendarStatus?.calendars || []).map((item) => (
                          <label key={item.id} className="google-sync-panel__calendar-check">
                            <input
                              type="checkbox"
                              checked={(googleSyncOptions?.busyCalendarIds || []).includes(item.id)}
                              onChange={() => handleToggleGoogleBusyCalendar(item.id)}
                              disabled={googleCalendarSyncing || googleCalendarActionLoading}
                            />
                            <span>{item.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className="google-sync-panel__range">
                    <span className="google-sync-panel__label">Окно импорта и экспорта</span>
                    <div className="google-sync-panel__range-controls">
                      <label className="google-sync-panel__range-item">
                        <span>Назад</span>
                        <input
                          className="google-sync-panel__control google-sync-panel__control--small"
                          type="number"
                          min={0}
                          max={60}
                          value={googleSyncOptions.daysBack}
                          onChange={(e) => handleGoogleSyncOptionChange("daysBack", e.target.value)}
                          disabled={googleCalendarSyncing || googleCalendarActionLoading}
                        />
                        <em>дней</em>
                      </label>

                      <label className="google-sync-panel__range-item">
                        <span>Вперёд</span>
                        <input
                          className="google-sync-panel__control google-sync-panel__control--small"
                          type="number"
                          min={1}
                          max={365}
                          value={googleSyncOptions.daysForward}
                          onChange={(e) => handleGoogleSyncOptionChange("daysForward", e.target.value)}
                          disabled={googleCalendarSyncing || googleCalendarActionLoading}
                        />
                        <em>дней</em>
                      </label>
                    </div>
                  </div>

                  <div className="google-sync-panel__actions">
                    <button
                      className="btn-sm"
                      type="button"
                      onClick={handleSaveGoogleCalendarPreferences}
                      disabled={googleCalendarSyncing || googleCalendarActionLoading}
                    >
                      {googleCalendarActionLoading ? "Сохраняем..." : "Сохранить настройки"}
                    </button>
                    <button
                      className="btn-sm"
                      type="button"
                      onClick={handleSyncGoogleCalendar}
                      disabled={googleCalendarSyncing || googleCalendarActionLoading}
                    >
                      {googleCalendarSyncing ? "Синхронизируем..." : "Запустить синхронизацию"}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="panel-card">
            <div className="panel-card-header">
              <span className="panel-card-title">Яндекс Календарь</span>
              <span className={`google-integration__header-status ${yandexStatusTone}`}>
                {yandexCalendarLoading ? "Загрузка..." : yandexConnected ? "Подключено" : "Не подключено"}
              </span>
            </div>
            <div className="panel-card-body google-integration">
              <div className={`google-integration__hero ${yandexStatusTone}`}>
                <div className="google-integration__hero-main">
                  <span className={`google-integration__badge ${yandexStatusTone}`}>{yandexStatusLabel}</span>
                  <h3 className="google-integration__title">{yandexHeroTitle}</h3>
                  <p className="google-integration__text">{yandexHeroText}</p>
                  {!yandexConnected ? (
                    <CalendarConsentCard
                      checked={yandexCalendarConsentChecked}
                      disabled={yandexCalendarActionLoading || !yandexEnabled || !yandexConfigured}
                      text={YANDEX_CALENDAR_CONSENT_TEXT}
                      version={YANDEX_CALENDAR_CONSENT_VERSION}
                      onToggle={setYandexCalendarConsentChecked}
                    />
                  ) : null}
                </div>

                <div className="google-integration__hero-actions">
                  {yandexConnected ? (
                    <>
                      <button
                        className="btn-sm"
                        type="button"
                        onClick={() => setYandexSyncConfiguratorOpen((prev) => !prev)}
                        disabled={yandexCalendarSyncing || yandexCalendarActionLoading}
                      >
                        {yandexSyncConfiguratorOpen ? "Скрыть параметры" : "Параметры синхронизации"}
                      </button>
                      <button
                        className="btn-sm danger"
                        type="button"
                        onClick={handleDisconnectYandexCalendar}
                        disabled={yandexCalendarActionLoading || yandexCalendarSyncing}
                      >
                        Отключить
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn-sm"
                      type="button"
                      onClick={handleConnectYandexCalendar}
                      disabled={
                        yandexCalendarActionLoading ||
                        !yandexCalendarConsentChecked ||
                        !yandexEnabled ||
                        !yandexConfigured
                      }
                    >
                      {yandexCalendarActionLoading ? "Подождите..." : "Подключить Яндекс Календарь"}
                    </button>
                  )}
                </div>
              </div>

              <div className="google-integration__stats">
                <div className="google-integration__stat-card">
                  <span className="google-integration__stat-label">Статус</span>
                  <strong className="google-integration__stat-value">{yandexStatusLabel}</strong>
                </div>
                <div className="google-integration__stat-card">
                  <span className="google-integration__stat-label">Аккаунт</span>
                  <strong className="google-integration__stat-value">
                    {yandexCalendarStatus?.accountEmail || "Пока не выбран"}
                  </strong>
                </div>
                <div className="google-integration__stat-card">
                  <span className="google-integration__stat-label">Календарь</span>
                  <strong className="google-integration__stat-value">
                    {yandexCalendarStatus?.calendarName || yandexCalendarStatus?.calendarUrl || "Будет выбран после подключения"}
                  </strong>
                </div>
                <div className="google-integration__stat-card">
                  <span className="google-integration__stat-label">Последняя синхронизация</span>
                  <strong className="google-integration__stat-value">
                    {yandexCalendarStatus?.lastSyncedAt
                      ? formatDateTime(yandexCalendarStatus.lastSyncedAt)
                      : "Ещё не запускалась"}
                  </strong>
                </div>
              </div>

              {!yandexConnected ? (
                <div className="google-integration__notice">
                  Подключение выполняется через страницу авторизации Яндекса. После подтверждения доступ вернёт вас обратно в раздел интеграций.
                </div>
              ) : null}

              {yandexCalendarStatus?.lastSyncMessage ? (
                <div className="google-integration__notice">
                  {yandexCalendarStatus.lastSyncMessage}
                </div>
              ) : null}

              {yandexConnected ? (
                <div className="google-integration__notice">
                  Бюро Души остаётся главным источником данных по сессиям: записи из Бюро Души создаются и обновляются в Яндекс Календаре, а личные события из Яндекса подтягиваются только как занятое время.
                </div>
              ) : null}

              {yandexConnected && yandexSyncConfiguratorOpen ? (
                <div className="google-sync-panel">
                  <div className="google-sync-panel__head">
                    <div>
                      <h4>Параметры синхронизации</h4>
                      <p>Настройте направление обмена и диапазон календарных событий.</p>
                    </div>
                  </div>

                  <div className="google-sync-panel__grid">
                    <label className="google-sync-panel__field">
                      <span className="google-sync-panel__label">Режим</span>
                      <select
                        className="google-sync-panel__control"
                        value={yandexSyncOptions.direction}
                        onChange={(e) => handleYandexSyncOptionChange("direction", e.target.value)}
                        disabled={yandexCalendarSyncing || yandexCalendarActionLoading}
                      >
                        {YANDEX_SYNC_DIRECTION_OPTIONS.map((item) => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </select>
                    </label>

                    {yandexSyncOptions.direction !== "TO_YANDEX" ? (
                      <label className="google-sync-panel__field">
                        <span className="google-sync-panel__label">Конфликты</span>
                        <select
                          className="google-sync-panel__control"
                          value={yandexSyncOptions.conflictPolicy}
                          onChange={(e) => handleYandexSyncOptionChange("conflictPolicy", e.target.value)}
                          disabled={yandexCalendarSyncing || yandexCalendarActionLoading}
                        >
                          {GOOGLE_SYNC_CONFLICT_OPTIONS.map((item) => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                  </div>

                  <div className="google-sync-panel__range">
                    <span className="google-sync-panel__label">Куда добавлять события при бронировании</span>
                    <label className="google-sync-panel__field">
                      <span className="google-sync-panel__label">Календарь по умолчанию</span>
                      <select
                        className="google-sync-panel__control"
                        value={yandexSyncOptions.exportCalendarUrl}
                        onChange={(e) => handleYandexSyncOptionChange("exportCalendarUrl", e.target.value)}
                        disabled={yandexCalendarSyncing || yandexCalendarActionLoading}
                      >
                        {(yandexCalendarStatus?.calendars || []).map((item) => (
                          <option key={item.id} value={item.id}>{item.label}</option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {(yandexCalendarStatus?.calendars || []).length ? (
                    <div className="google-sync-panel__range">
                      <span className="google-sync-panel__label">Какие календари учитывать как занятость</span>
                      <div className="google-sync-panel__calendar-list">
                        {(yandexCalendarStatus?.calendars || []).map((item) => (
                          <label key={item.id} className="google-sync-panel__calendar-check">
                            <input
                              type="checkbox"
                              checked={(yandexSyncOptions?.busyCalendarUrls || []).includes(item.id)}
                              onChange={() => handleToggleYandexBusyCalendar(item.id)}
                              disabled={yandexCalendarSyncing || yandexCalendarActionLoading}
                            />
                            <span>{item.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className="google-sync-panel__range">
                    <span className="google-sync-panel__label">Окно импорта и экспорта</span>
                    <div className="google-sync-panel__range-controls">
                      <label className="google-sync-panel__range-item">
                        <span>Назад</span>
                        <input
                          className="google-sync-panel__control google-sync-panel__control--small"
                          type="number"
                          min={0}
                          max={60}
                          value={yandexSyncOptions.daysBack}
                          onChange={(e) => handleYandexSyncOptionChange("daysBack", e.target.value)}
                          disabled={yandexCalendarSyncing || yandexCalendarActionLoading}
                        />
                        <em>дней</em>
                      </label>

                      <label className="google-sync-panel__range-item">
                        <span>Вперёд</span>
                        <input
                          className="google-sync-panel__control google-sync-panel__control--small"
                          type="number"
                          min={1}
                          max={365}
                          value={yandexSyncOptions.daysForward}
                          onChange={(e) => handleYandexSyncOptionChange("daysForward", e.target.value)}
                          disabled={yandexCalendarSyncing || yandexCalendarActionLoading}
                        />
                        <em>дней</em>
                      </label>
                    </div>
                  </div>

                  <div className="google-sync-panel__actions">
                    <button
                      className="btn-sm"
                      type="button"
                      onClick={handleSaveYandexCalendarPreferences}
                      disabled={yandexCalendarSyncing || yandexCalendarActionLoading}
                    >
                      {yandexCalendarActionLoading ? "Сохраняем..." : "Сохранить настройки"}
                    </button>
                    <button
                      className="btn-sm"
                      type="button"
                      onClick={handleSyncYandexCalendar}
                      disabled={yandexCalendarSyncing || yandexCalendarActionLoading}
                    >
                      {yandexCalendarSyncing ? "Синхронизируем..." : "Запустить синхронизацию"}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {canManageQuasarIntegration ? (
        <div className="right-col">
          <div className="panel-card">
            <div className="panel-card-header">
              <span className="panel-card-title">Quasar</span>
              <span className="meta-small">
                {quasarLoading ? "Загрузка..." : quasar?.enabled ? "Подключено" : "Не подключено"}
              </span>
            </div>
            <div className="panel-card-body panel-card-body--rows">
              <div className="field-actions">
                {quasar?.enabled ? (
                  <button
                    className="btn-sm danger"
                    type="button"
                    onClick={handleDisconnectQuasar}
                    disabled={quasarActionLoading}
                  >
                    Отключить
                  </button>
                ) : (
                  <button
                    className="btn-sm"
                    type="button"
                    onClick={handleConnectQuasar}
                    disabled={quasarActionLoading}
                  >
                    {quasarActionLoading ? "Подождите..." : "Подключить Quasar"}
                  </button>
                )}
              </div>

              {quasar?.connectUrl || quasar?.qrCode || quasar?.connectExpiresAt || quasar?.webhookMessage ? (
                <div className="empty-row">
                  {sanitizeLinkHref(quasar?.connectUrl, {
                    allowRelative: false,
                    allowMailto: false,
                    allowTel: false,
                    allowHash: false,
                  }) ? (
                    <a
                      href={sanitizeLinkHref(quasar?.connectUrl, {
                        allowRelative: false,
                        allowMailto: false,
                        allowTel: false,
                        allowHash: false,
                      })}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Открыть ссылку подключения
                    </a>
                  ) : null}
                  {quasar?.connectExpiresAt ? (
                    <div>Действует до: {formatDateTime(quasar.connectExpiresAt)}</div>
                  ) : null}
                  {typeof quasar?.webhookConfigured === "boolean" ? (
                    <div>Webhook: {quasar.webhookConfigured ? "настроен" : "не настроен"}</div>
                  ) : null}
                  {quasar?.webhookMessage ? <div>{quasar.webhookMessage}</div> : null}
                </div>
              ) : null}

              {quasar?.enabled ? (
                <>
                  {QUASAR_NOTIFICATION_FIELDS.map((row) => (
                    <div className="toggle-row" key={row.key}>
                      <div>
                        <div className="toggle-row__title">{row.label}</div>
                        <div className="toggle-row__sub">{row.description}</div>
                      </div>
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(quasar?.notifications?.[row.key])}
                          disabled={quasarActionLoading}
                          onChange={(e) => handleToggleQuasarField(row.key, e.target.checked)}
                        />
                        <span className="toggle-track" />
                        <span className="toggle-thumb" />
                      </label>
                    </div>
                  ))}
                </>
              ) : null}
            </div>
          </div>
        </div>
        ) : null}
      </div>
    );
  };

  const renderTabSettings = () => (
    <div className="tab-panel active" id="tab-settings">
      <div className="left-col">
        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Тема интерфейса</span>
          </div>
          <div className="panel-card-body">
            <div className="theme-options">
              <button
                type="button"
                className={`theme-option ${theme === "light" ? "selected" : ""}`}
                onClick={() => handleThemeChange("light")}
                disabled={themeSaving}
              >
                <span className="theme-preview light" />
                <span>
                  <span className="theme-name">Светлая</span>
                  <span className="theme-sub">По умолчанию</span>
                </span>
                <span className="theme-check" />
              </button>

              <button
                type="button"
                className={`theme-option ${theme === "dark" ? "selected" : ""}`}
                onClick={() => handleThemeChange("dark")}
                disabled={themeSaving}
              >
                <span className="theme-preview dark" />
                <span>
                  <span className="theme-name">Тёмная</span>
                  <span className="theme-sub">Ночной режим</span>
                </span>
                <span className="theme-check" />
              </button>
            </div>
          </div>
        </div>

        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Настройки уведомлений</span>
            <Link className="btn-sm" to="/notifications">Расширенные →</Link>
          </div>
          <div className="panel-card-body panel-card-body--rows">
            {NOTIFICATION_TOGGLES.map((item) => (
              <div className="toggle-row" key={item.key}>
                <div>
                  <div className="toggle-row__title">{item.icon} {item.title}</div>
                  <div className="toggle-row__sub">{item.subtitle}</div>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={Boolean(notificationSettings?.[item.key])}
                    disabled={savingNotificationKey === item.key}
                    onChange={(e) => handleToggleNotification(item.key, e.target.checked)}
                  />
                  <span className="toggle-track" />
                  <span className="toggle-thumb" />
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Подсказки и онбординг</span>
          </div>
          <div className="panel-card-body panel-card-body--rows">
            <div className="toggle-row">
              <div>
                <div className="toggle-row__title">Запустить стартовые шаги заново</div>
                <div className="toggle-row__sub">Если вы скрыли подсказки на главной или хотите пройти их ещё раз, включите онбординг снова.</div>
              </div>
              <button type="button" className="btn-sm" onClick={handleRestartOnboarding} disabled={restartingOnboarding}>
                {restartingOnboarding ? "Запускаем..." : "Запустить"}
              </button>
            </div>
          </div>
        </div>

        {isPsychologist ? (
          <div className="panel-card">
            <div className="panel-card-header">
              <span className="panel-card-title">Кто может писать вам в чат</span>
              <span className="meta-small">
                {savingPsychologistChatPolicy ? "Сохраняем..." : "Применяется сразу"}
              </span>
            </div>
            <div className="panel-card-body chat-policy-list">
              {loadingPsychologistChatPolicy ? (
                <div className="empty-row">Загрузка...</div>
              ) : (
                CHAT_POLICY_OPTIONS.map((option) => {
                  const isActive = psychologistChatPolicy === option.key;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      className={`chat-policy-option ${
                        isActive ? "is-active" : ""
                      }`}
                      disabled={savingPsychologistChatPolicy}
                      onClick={() => handlePsychologistChatPolicyChange(option.key)}
                    >
                      <span className="chat-policy-option__head">
                        <span className={`chat-policy-option__icon is-${option.accent}`} aria-hidden="true">
                          {option.accent === "mint" ? "✓" : option.accent === "lav" ? "◐" : "−"}
                        </span>
                        <span className="chat-policy-option__copy">
                          <span className="chat-policy-option__title-row">
                            <span className="chat-policy-option__title">{option.title}</span>
                            {isActive ? (
                              <span className="chat-policy-option__badge">Выбрано</span>
                            ) : null}
                          </span>
                          <span className="chat-policy-option__sub">{option.subtitle}</span>
                        </span>
                        <span className={`chat-policy-option__radio ${isActive ? "is-active" : ""}`} aria-hidden="true">
                          <span />
                        </span>
                      </span>
                      <span className="chat-policy-option__foot">
                        <span className={`chat-policy-option__chip is-${option.accent}`}>{option.badge}</span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </div>

      <div className="right-col">
        <div className="panel-card">
          <div className="panel-card-header">
            <span className="panel-card-title">Push-устройства</span>
          </div>
          <div className="panel-card-body panel-card-body--rows">
            {pushDevicesLoading ? <div className="empty-row">Загрузка...</div> : null}
            {!pushDevicesLoading && pushDevices.length === 0 ? <div className="empty-row">Подключенных устройств нет</div> : null}
            {!pushDevicesLoading && pushDevices.map((device) => {
              const key = `${device.channel || ""}:${device.id || ""}`;
              const name = `${device.browser || "Браузер"} · ${device.platform || "Устройство"}`;
              const channel = device.channel === "fcm" ? "FCM" : "Web Push";
              return (
                <div className="push-device" key={key}>
                  <div className="push-device__left">
                    <div className="sub-avatar">{device.platform?.toLowerCase().includes("ios") ? "📱" : "💻"}</div>
                    <div className="sub-body">
                      <div className="sub-name">{name}</div>
                      <div className="sub-type">{channel} · {device.active ? "Активно" : "Неактивно"}</div>
                    </div>
                  </div>
                  <button
                    className="btn-sm danger"
                    type="button"
                    onClick={() => handleDisconnectDevice(device)}
                    disabled={disconnectingDevice === key}
                  >
                    Отключить
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="b-client-profile podcasts-hub">
      <div className="shell shell--shared podcasts-shell">
        <AppSidebarV2
          activeKey="cabinet"
          mobileOpen={mobileSidebarOpen}
          onCloseMobile={() => setMobileSidebarOpen(false)}
          userName={fullName || me?.name || me?.login || "Пользователь"}
          roleLabel={roleLabel(me?.role)}
          canOpenLiteratureEditor={canManageLiterature}
          notificationsBadge={unreadNotificationsCount}
          onLogout={handleLogout}
        />

        <button
          className={`podcasts-overlay ${mobileSidebarOpen ? "is-open" : ""}`}
          type="button"
          onClick={() => setMobileSidebarOpen(false)}
          aria-label="Закрыть меню"
        />

        <div className="main podcasts-main">
          <div className="topbar">
            <div className="topbar-left">
              <button className="btn-icon" type="button" id="hamburger" onClick={() => setMobileSidebarOpen(true)}>
                <span>☰</span>
              </button>
              <span className="topbar-title">Мой профиль</span>
            </div>
            <div className="topbar-right">
              <button className="btn-sm" type="button" onClick={handleLogout}>Выйти</button>
            </div>
          </div>

          <div className="profile-tabs">
            {profileTabs.map((tab) => (
              <button
                key={tab.id}
                className={`profile-tab ${activeTab === tab.id ? "active" : ""}`}
                type="button"
                onClick={() => switchTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="content">
            {activeTab === "profile" ? renderTabProfile() : null}
            {activeTab === "appointments" ? renderTabAppointments() : null}
            {activeTab === "subscriptions" ? renderTabSubscriptions() : null}
            {activeTab === "content" ? renderTabContent() : null}
            {activeTab === "psychologist" && isPsychologist ? renderTabPsychologist() : null}
            {activeTab === "integrations" && canManageIntegrations ? renderTabIntegrations() : null}
            {activeTab === "settings" ? renderTabSettings() : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
